/**
 * A deliberately simple timeline: graphics, video layers (V2 and up), the V1 storyline with its transitions and
 * linked source audio (A1), the audio tracks (A2 and, 1.7.3, A3–A8) and subtitles. Every edit is a single undoable
 * document operation applied on pointer release; positions snap to the sequence frame grid.
 *
 * 0.96.0: markers on the ruler; fade handles at the clips' top corners (picture and sound together); waveforms on
 * sound clips; mute and solo per sound track; snapping to clip edges, the playhead and markers (N, the magnet);
 * zoom to fit (\), zoom around the playhead (slider, = and −) or the pointer (Ctrl/pinch wheel); the view follows
 * the playhead during playback (page by page) until you scroll or drag; the playhead line and timecode come from
 * the playhead clock, so playback does not re-render the editor.
 *
 * 1.7.2: the Select (V), Slip (Y) and Slide (U) tools; Option (Alt)-dragging a clip's body slips it with any tool. A
 * slip or slide previews from local state (and in the sequence viewer's trim view) and is one edit on release.
 *
 * 1.7.3 (docs/dev/editor-1.7.3-build.md): several items selected at once (selection.ts: Shift- or ⌘-click, a rubber
 * band on empty lane space, linked groups together, Option-click one); a V1 body drag places freely (overwrite,
 * ⌘-drag inserts) with a drop preview; several items move together; V1 edges trim without rippling (⌘ ripples);
 * gaps on V1 are drawn and selectable; GFX 1–3 and A2–A8 lanes with their headers; a context menu (Shift+F10 too).
 *
 * 1.7.4 (docs/dev/editor-1.7.4-build.md): the Ripple (B), Roll (⇧B), Blade (C) and Select forward (A) tools; a
 * picture dragged up or down moves between V1 and the video tracks with its linked sound (ops.moveToVideoTrack), a
 * drop past the last video, graphics or audio track makes one (the dashed "New track" lane); the sequence In and Out
 * on the ruler; Source V / Source A targets and the track gain in the headers; the level line (rubber band) with
 * volume keys on sound clips and the crossfades at cuts.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent as RMouseEvent, type PointerEvent as RPointerEvent } from 'react';
import { ArrowLeftRight, ArrowRightToLine, Blend, Captions, Columns2, Eye, EyeOff, Film, Layers, Link2, Magnet, Maximize, MousePointer2, MoveHorizontal, MoveRight, Music, Plus, Scissors, Trash2, UnfoldHorizontal, Video, Volume2, VolumeX } from 'lucide-react';
import {
  CROSSFADE_ALIGN_LABELS, MAX_AUDIO_TRACKS, MAX_GRAPHIC_TRACKS, MAX_LAYERS, TICKS_PER_SECOND, TRANSITION_KINDS, TRANSITION_LABELS, allAudioTracks, audioTrackOf, clipDuration, framesToTimecode, formatTimecode, gapsOf,
  graphicTrackCount, graphicTrackOf, itemKey, itemSpan, layoutVideo, ops, pictureFades, sequenceDuration, snap, storylinePictureFades,
  cueRuns, runsText, ticksPerFrame, ticksToFrames, trackAudible, ografIsSting, ografLabel, selectForward, trackGainDb, type EditTargets, type GraphicTemplateDTO, type GraphicTrack, type ItemRef,
  type SequenceDocument, type TrackRef, type TransitionKind,
} from '@prism/contracts';
import { GRAPHIC_DRAG_TYPE, readGraphicDrag, type GraphicDrop } from '../../lib/graphic-drag';
import { isOgrafTemplate, newerVersion, templateFor } from '../../graphics/ograf';
import { isTyping } from '../../lib/keys';
import { usePref } from '../../lib/prefs';
import type { SequenceSource } from '../../api/hooks';
import { useClockValue, type PlayheadClock } from './playheadClock';
import { TimecodeField } from './TimecodeField';
import { fitZoom, followScroll, incomingAfter, outgoingOf, rulerTicksUnderMarkers, signedTimecode, snapCandidates, snapDelta, trimEdit, zoomScroll, type TimelineTool, type TrimTrack } from './timelineTools';
import type { TrimPreviewStore } from './TrimView';
import { useFormatView } from './formatContext';
import { ReframeLane, ReframeLaneLabel } from './ReframeLane';
import { addKeyAtPlayhead, adjacentKeyTime, formatColor, laneKeys, showsReframeLane } from './edit-formats';
import { toast } from '../../components/Toasts';
// 1.8.0: the © mark on clips whose source's rights are red or amber (beside REC and CC).
import { RightsMark } from '../../components/RightsMark';
import { ContextMenu, type ContextMenuEntry } from '../../components/ContextMenu';
import { NO_SELECTION, boxOf, isSelected, isTinyBox, itemsInBox, parseItemKey, pressItem, selectOnly, addItems, groupOf, uniqueItems, type Box, type TimelineSelection } from './selection';
import { LevelLine } from './LevelLine';
import { CueText } from '../../subtitles/CueOverlay';
import { crossfadeMarks, isSilent, sourceLengths } from './audioPreview';
import { audioDrop, audioIndexOf, graphicDrop, notesToast, primaryFirst, videoDrop, videoLaneOf } from './trackDrag';

/** One item (1.7.3: the primary item of the timeline's selection), as the inspectors name it. */
export type Selection = ItemRef | null;

type DragKind = 'move-video' | 'trim-in' | 'trim-out' | 'move-audio' | 'move-graphic' | 'resize-graphic' | 'move-cue' | 'scrub' | 'move-layer' | 'trim-layer-in' | 'trim-layer-out'
  | 'trim-cue-in' | 'trim-cue-out' | 'transition-in' | 'transition-out' | 'move-step' | 'fade-in' | 'fade-out' | 'move-marker'
  | 'slip-video' | 'slip-layer' | 'slip-audio' | 'slide-video' | 'slide-layer' | 'slide-audio'
  // 1.7.3: a press that only selects (a gap, a read-only sequence), several items moved together, an audio clip's edges.
  | 'press' | 'move-items' | 'trim-audio-in' | 'trim-audio-out'
  // 1.7.4: the Ripple tool's edge drags (any track) and the Roll tool's cut drag (the incoming clip).
  | 'ripple-in' | 'ripple-out' | 'roll';

/** A slip or slide drag's mode (1.7.2), or null for other drags. */
const trimMode = (kind: DragKind): 'slip' | 'slide' | null => (kind.startsWith('slip-') ? 'slip' : kind.startsWith('slide-') ? 'slide' : null);

/** The edge-trim drags of each track (1.7.3), by side; with ⌘ they ripple off V1 too (1.7.4). */
const EDGE_TRIMS = new Set<DragKind>(['trim-in', 'trim-out', 'trim-layer-in', 'trim-layer-out', 'trim-audio-in', 'trim-audio-out', 'resize-graphic', 'trim-cue-in', 'trim-cue-out']);
const isInEdge = (k: DragKind) => k.endsWith('-in');

/** Run an op the drag preview draws; a refusing op (or one stream A has not written yet) draws nothing. */
function attempt<T>(f: () => T): T | null {
  try { return f(); } catch { return null; }
}

/** The track an item is on (1.7.4: Select forward with ⇧ and Blade). */
function trackOfItem(doc: SequenceDocument, ref: ItemRef): TrackRef | undefined {
  if (ref.kind === 'video') return { kind: 'v1' };
  if (ref.kind === 'layer') { const l = (doc.layers ?? []).find((x) => x.clips.some((c) => c.id === ref.id)); return l ? { kind: 'layer', id: l.id } : undefined; }
  if (ref.kind === 'audio') { const t = audioTrackOf(doc, ref.id); return t ? { kind: 'audio', id: t } : undefined; }
  if (ref.kind === 'graphic') { const g = doc.graphics.find((x) => x.id === ref.id); return g ? { kind: 'graphic', track: graphicTrackOf(g) } : undefined; }
  return undefined;
}

/** The single-item move drags and the kind of item each moves (1.7.3: with several selected, they become 'move-items'). */
const MOVE_KIND: Partial<Record<DragKind, ItemRef['kind']>> = {
  'move-video': 'video', 'move-audio': 'audio', 'move-layer': 'layer', 'move-graphic': 'graphic', 'move-cue': 'cue', 'move-marker': 'marker',
};

/** The tools (1.7.2): label, key and icon of each toolbar button. */
const TOOLS: Array<{ id: TimelineTool; label: string; key: string; title: string; Icon: typeof MousePointer2 }> = [
  { id: 'select', label: 'Select', key: 'V', title: 'Select, move and trim clips', Icon: MousePointer2 },
  { id: 'slip', label: 'Slip', key: 'Y', title: 'Slip: drag a clip to show other footage in the same place and length (or Option-drag with any tool)', Icon: ArrowLeftRight },
  { id: 'slide', label: 'Slide', key: 'U', title: 'Slide: drag a clip along the track; its neighbours get longer or shorter to match', Icon: MoveHorizontal },
  { id: 'ripple', label: 'Ripple', key: 'B', title: 'Ripple: drag an edge to trim; what is after it on the track moves with it (with Sync, the other tracks too)', Icon: UnfoldHorizontal },
  { id: 'roll', label: 'Roll', key: '⇧B', title: 'Roll: drag a cut between two touching clips; one gets longer as the other gets shorter', Icon: Columns2 },
  { id: 'blade', label: 'Blade', key: 'C', title: 'Blade: click a clip to split it there (⇧-click splits every track)', Icon: Scissors },
  { id: 'forward', label: 'Select forward', key: 'A', title: 'Select forward: click to select everything from there to the end (⇧-click: that track only)', Icon: ArrowRightToLine },
];

interface DragState {
  kind: DragKind;
  id: string;
  startX: number;
  dx: number;
  startY: number;
  dy: number;
  /** The move in ticks, on the frame grid and snapped (0.96.0). */
  dt: number;
  /** 1.7.3: the item pressed, what a click (no drag) selects, the selection a drag moves, and whether it has moved. */
  grabbed?: ItemRef;
  click?: TimelineSelection | null;
  dragSel?: TimelineSelection;
  moved?: boolean;
  /** 1.7.3: ⌘ (Ctrl) held: a V1 body drag inserts, a V1 edge drag ripples. */
  cmd?: boolean;
}

/** A rubber band in progress (1.7.3), in the lanes' content coordinates. */
interface Band { x0: number; y0: number; x1: number; y1: number; add: boolean; base: TimelineSelection }

const LANE = 34;
const S = TICKS_PER_SECOND;
/** Snapping reaches this far (pixels). */
const SNAP_PX = 8;
const MIN_PPS = 2;
const MAX_PPS = 240;

/* ---------------------------------------------------------------- waveforms (0.96.0) */

interface Waveform { sampleRate: number; samplesPerPeak: number; length: number; peaks: number[] }
const waveCache = new Map<string, Promise<Waveform | null>>();
/** A source's waveform, fetched once per version (the signed URL changes, the waveform does not). */
function loadWave(versionId: string, url: string): Promise<Waveform | null> {
  let p = waveCache.get(versionId);
  if (!p) {
    p = fetch(url).then((r) => (r.ok ? r.json() : null)).catch(() => null);
    // A failed fetch is not remembered (1.0.0-rc.4): the next signed URL tries again.
    const mine = p;
    void p.then((w) => { if (!w && waveCache.get(versionId) === mine) waveCache.delete(versionId); });
    waveCache.set(versionId, p);
  }
  return p;
}

/** A sound clip's waveform from its in point to its out point, scaled by its gain. */
function ClipWave({ source, inTicks, outTicks, width, gainDb, className }: { source: SequenceSource | undefined; inTicks: number; outTicks: number; width: number; gainDb: number; className?: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [wave, setWave] = useState<Waveform | null>(null);
  const url = source?.waveformUrl ?? null;
  const versionId = source?.versionId;
  useEffect(() => {
    if (!url || !versionId) { setWave(null); return; }
    let live = true;
    void loadWave(versionId, url).then((w) => { if (live) setWave(w); });
    return () => { live = false; };
  }, [url, versionId]);
  const w = Math.max(1, Math.min(4000, Math.round(width)));
  const pending = Boolean(source?.live) && !url;
  useEffect(() => {
    const c = ref.current;
    if (!c || !wave?.length) return;
    const h = c.clientHeight || 24;
    const dpr = window.devicePixelRatio || 1;
    c.width = w * dpr;
    c.height = h * dpr;
    const ctx = c.getContext('2d')!;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, w, h);
    const perPeak = wave.samplesPerPeak / wave.sampleRate;
    const from = inTicks / S / perPeak;
    const to = outTicks / S / perPeak;
    const gain = Math.pow(10, gainDb / 20);
    const mid = h / 2;
    ctx.fillStyle = 'rgba(255,255,255,.42)';
    for (let x = 0; x < w; x++) {
      const i0 = Math.floor(from + ((to - from) * x) / w);
      const i1 = Math.max(i0 + 1, Math.floor(from + ((to - from) * (x + 1)) / w));
      let lo = 0;
      let hi = 0;
      for (let i = i0; i < i1 && i < wave.length; i++) {
        lo = Math.min(lo, wave.peaks[i * 2] ?? 0);
        hi = Math.max(hi, wave.peaks[i * 2 + 1] ?? 0);
      }
      const top = Math.min(1, (hi / 127) * gain);
      const bottom = Math.min(1, (-lo / 127) * gain);
      ctx.fillRect(x, mid - top * (mid - 1), 1, Math.max(1, (top + bottom) * (mid - 1)));
    }
  }, [wave, w, inTicks, outTicks, gainDb]);
  // A recording (1.0.0-rc.1) has no waveform until it ends: a placeholder with the REC mark instead.
  if (pending) {
    return (
      <span className={`tl-wave tl-wave-pending ${className ?? ''}`} data-testid="clip-recording" title={source?.live?.state === 'recording' ? 'Recording: the waveform appears when the recording ends' : 'The recording has ended; the waveform appears when its file is processed'}>
        <span className="rec-badge">● REC</span>
      </span>
    );
  }
  if (!url) return null;
  return <canvas ref={ref} className={`tl-wave ${className ?? ''}`} data-testid="clip-waveform" data-ready={wave ? 'true' : 'false'} aria-hidden />;
}

/** The sequence timecode, live during playback (from the clock), typed to jump. */
function LiveTimecode({ clock, rate, onJump, total }: { clock: PlayheadClock; rate: SequenceDocument['frameRate']; onJump: (t: number) => void; total: number }) {
  const ph = useClockValue(clock);
  const tc = formatTimecode(framesToTimecode(ticksToFrames(Math.min(ph, Math.max(total, 0)), rate), rate));
  return <TimecodeField value={tc} rate={rate} onJump={onJump} className="mono" testId="playhead-tc" />;
}

/** Every clip on any track by id: V1, the layers and the audio tracks (A2–A8). */
function clipOf(doc: SequenceDocument, id: string) {
  return doc.video.find((c) => c.id === id)
    ?? (doc.layers ?? []).flatMap((l) => l.clips).find((c) => c.id === id)
    ?? allAudioTracks(doc).flatMap((t) => t.clips).find((c) => c.id === id);
}

/** A small chain on a linked clip or graphic (1.7.3). */
const LinkMark = ({ linkId }: { linkId?: string }) => (linkId ? <Link2 size={10} className="tl-link-mark" data-testid="clip-linked" aria-label="Linked" /> : null);

export function Timeline({ doc, apply, seal, playhead, setPlayhead, selection, setSelection, sourceDurations, pps, setPps, canEdit = true, onDropGraphic, templates = [],
  clock, playing = false, sources, transitionKind, setTransitionKind, onAddTransition, tool = 'select', setTool, trimPreview,
  sync = true, setSync, hiddenGfx = [], setHiddenGfx, menuFor, targets, setTargets }: {
  doc: SequenceDocument;
  apply: (fn: (d: SequenceDocument) => SequenceDocument, coalesce?: string) => void;
  /** End an undo gesture (0.96.0). */
  seal?: () => void;
  playhead: number;
  setPlayhead: (ticks: number) => void;
  /** 1.7.3: the items selected (selection.ts). */
  selection: TimelineSelection;
  setSelection: (s: TimelineSelection) => void;
  sourceDurations: Map<string, number>;
  pps: number;
  setPps: (n: number) => void;
  canEdit?: boolean;
  /** A graphic (a built-in template or a graphics asset) dropped on a graphics track (1.7.3: which one), at this time. */
  onDropGraphic?: (drop: GraphicDrop, atTicks: number, track: GraphicTrack) => void;
  /** Templates, for OGraf clips' labels, step marks, fixed lengths and newer versions (0.93.0). */
  templates?: GraphicTemplateDTO[];
  /** The live playhead (0.96.0). */
  clock: PlayheadClock;
  playing?: boolean;
  /** The sources by version id, for waveforms. */
  sources?: Map<string, SequenceSource>;
  transitionKind: TransitionKind;
  setTransitionKind: (k: TransitionKind) => void;
  onAddTransition: (k: TransitionKind) => void;
  /** 1.7.2: the tool (kept by the editor, not saved), and where a slip or slide in progress is shown (the trim view). */
  tool?: TimelineTool;
  setTool?: (t: TimelineTool) => void;
  trimPreview?: TrimPreviewStore;
  /** 1.7.3: the Sync toggle (ripples move the other tracks too; usePref editor.syncRipple, kept by the editor). */
  sync?: boolean;
  setSync?: (v: boolean) => void;
  /** 1.7.3: graphics tracks hidden in the preview (0-based; a view-only pref, editor.hiddenGfx; exports draw them all). */
  hiddenGfx?: number[];
  setHiddenGfx?: (v: number[]) => void;
  /** 1.7.3: the context menu of a selection (the editor's commands). */
  menuFor?: (sel: TimelineSelection) => ContextMenuEntry[];
  /** 1.7.4: the source patch (Source V / Source A in the headers), resolved for this document, and how to change it. */
  targets?: EditTargets;
  setTargets?: (t: EditTargets) => void;
}) {
  const [drag, setDrag] = useState<DragState | null>(null);
  const [band, setBand] = useState<Band | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; sel: TimelineSelection } | null>(null);
  const [dropOver, setDropOver] = useState<GraphicTrack | null>(null);
  // 1.7.4: the selected volume key (Delete removes it), and a sequence In or Out being dragged on the ruler.
  const [selKey, setSelKey] = useState<{ clipId: string; index: number } | null>(null);
  const [markDrag, setMarkDrag] = useState<{ which: 'in' | 'out'; startX: number; from: number; to: number } | null>(null);
  const [snapping, setSnapping] = usePref('editor.snap', true);
  const [follow, setFollow] = usePref('editor.follow', true);
  const lanes = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const labels = useRef<HTMLDivElement>(null);
  const playheadLine = useRef<HTMLDivElement>(null);
  const candidates = useRef<number[]>([]);
  const placed = useMemo(() => layoutVideo(doc), [doc]);
  const vfades = useMemo(() => storylinePictureFades(doc, placed), [doc, placed]);
  const layers = doc.layers ?? [];
  const audioTracks = useMemo(() => allAudioTracks(doc), [doc]);
  const gfxCount = graphicTrackCount(doc);
  const total = sequenceDuration(doc);
  const width = Math.max(1200, ((total / S) + 30) * pps);
  const x = (ticks: number) => (ticks / S) * pps;
  const tc = (ticks: number) => formatTimecode(framesToTimecode(ticksToFrames(ticks, doc.frameRate), doc.frameRate));
  const tpf = ticksPerFrame(doc.frameRate);
  const ppsRef = useRef(pps);
  ppsRef.current = pps;
  // Whether an item is selected, by its key (⌘A can select hundreds).
  const selKeys = useMemo(() => new Set(selection.items.map(itemKey)), [selection]);
  const sel = (kind: ItemRef['kind'], id: string) => selKeys.has(itemKey({ kind, id }));
  // 1.7.0: the Reframe lane, for the active format, when the sequence has formats or any clip has keys.
  const formatView = useFormatView(doc);
  const reframe = showsReframeLane(doc);
  const primary = selection.primary;
  const selectedPicture = primary && (primary.kind === 'video' || primary.kind === 'layer') ? primary.id : null;
  const reframeAdd = () => {
    const a = addKeyAtPlayhead(doc, formatView.active, selectedPicture, playhead);
    if (!a) { toast('Move the playhead over a clip to add a key', 'error'); return; }
    apply(a.edit.fn, a.edit.coalesce);
    seal?.();
  };
  const reframeGo = (dir: -1 | 1) => {
    const t = adjacentKeyTime(laneKeys(doc, formatView.active, selectedPicture), playhead, dir, ticksPerFrame(doc.frameRate));
    if (t !== null) setPlayhead(t);
  };

  const pointerTicks = useCallback((clientX: number) => {
    const r = lanes.current!.getBoundingClientRect();
    return Math.max(0, snap(((clientX - r.left + lanes.current!.scrollLeft) / pps) * S, doc.frameRate));
  }, [pps, doc.frameRate]);

  /* ---------------------------------------------- the live playhead, following it (0.96.0) */

  const manual = useRef(false);
  const followRef = useRef(follow);
  followRef.current = follow;
  const playingRef = useRef(playing);
  playingRef.current = playing;
  useEffect(() => { if (playing) manual.current = false; }, [playing]);
  useEffect(() => {
    const place = (t: number) => {
      const px = (t / S) * ppsRef.current;
      if (playheadLine.current) playheadLine.current.style.left = `${px}px`;
      const el = lanes.current;
      if (!el || !playingRef.current || !followRef.current || manual.current) return;
      const to = followScroll(px, el.scrollLeft, el.clientWidth);
      if (to !== null) el.scrollLeft = to;
    };
    place(clock.get());
    return clock.subscribe(place);
  }, [clock]);

  /* ---------------------------------------------- zoom (0.96.0) */

  const pendingScroll = useRef<number | null>(null);
  /** Zoom keeping the time under `anchorX` (pixels from the lanes' left edge) where it is; by default the playhead. */
  const zoomTo = useCallback((next: number, anchor?: { ticks: number; x: number }) => {
    const el = lanes.current;
    const p = Math.max(MIN_PPS, Math.min(MAX_PPS, next));
    if (!el) { setPps(p); return; }
    const a = anchor ?? { ticks: clock.get(), x: (clock.get() / S) * ppsRef.current - el.scrollLeft };
    pendingScroll.current = zoomScroll(a.ticks, a.x, p, S);
    setPps(p);
  }, [setPps, clock]);
  useLayoutEffect(() => {
    if (pendingScroll.current !== null && lanes.current) { lanes.current.scrollLeft = pendingScroll.current; pendingScroll.current = null; }
  }, [pps]);
  const zoomToFit = useCallback(() => {
    const el = lanes.current;
    if (!el) return;
    pendingScroll.current = 0;
    setPps(fitZoom(total, el.clientWidth, S, MIN_PPS, MAX_PPS));
  }, [setPps, total]);
  // Ctrl/⌘ + wheel and trackpad pinch (which browsers send as Ctrl + wheel) zoom around the pointer.
  useEffect(() => {
    const el = lanes.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      manual.current = true;
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const ax = e.clientX - r.left;
      const ticks = ((ax + el.scrollLeft) / ppsRef.current) * S;
      zoomTo(ppsRef.current * Math.exp(-e.deltaY * 0.01), { ticks, x: ax });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomTo]);

  /* ---------------------------------------------- the context menu (1.7.3) */

  /** Open the menu for a selection at a point (a right-click, or Shift+F10 at the primary item). */
  const openMenu = useCallback((at: { x: number; y: number }, s: TimelineSelection) => {
    if (!menuFor) return;
    setMenu({ ...at, sel: s });
  }, [menuFor]);
  const onItemMenu = (e: RMouseEvent, ref: ItemRef | null) => {
    if (!menuFor) return;
    e.preventDefault();
    e.stopPropagation();
    const s = !ref ? NO_SELECTION : isSelected(selection, ref) ? { ...selection, primary: ref } : selectOnly(doc, ref, e.altKey);
    if (s !== selection) setSelection(s);
    openMenu({ x: e.clientX, y: e.clientY }, s);
  };

  // Keys: \ zoom to fit, = / − zoom around the playhead, N snapping on or off; 1.7.3: Shift+F10 or the menu key opens
  // the context menu of the selection.
  const selRef = useRef(selection);
  selRef.current = selection;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isTyping(e, 'input, textarea, select, [contenteditable]')) return;
      if ((e.key === 'F10' && e.shiftKey) || e.key === 'ContextMenu') {
        const s = selRef.current;
        if (!menuFor) return;
        e.preventDefault();
        const el = s.primary ? lanes.current?.querySelector(`[data-item="${CSS.escape(itemKey(s.primary))}"]`) : null;
        const r = el?.getBoundingClientRect() ?? lanes.current?.getBoundingClientRect();
        if (r) openMenu({ x: r.left + Math.min(24, r.width / 2), y: r.top + Math.min(r.height, 30) }, s);
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === '\\') { e.preventDefault(); zoomToFit(); }
      else if (e.key === '=' || e.key === '+') { e.preventDefault(); zoomTo(ppsRef.current * 1.25); }
      else if (e.key === '-' || e.key === '_') { e.preventDefault(); zoomTo(ppsRef.current / 1.25); }
      else if (e.key.toLowerCase() === 'n' && !e.shiftKey) { e.preventDefault(); setSnapping(!snapping); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoomTo, zoomToFit, snapping, setSnapping, menuFor, openMenu]);

  // 1.7.4: Delete or Backspace on a selected volume key removes it (before the editor's Delete, as the Reframe lane).
  const selKeyRef = useRef(selKey);
  selKeyRef.current = selKey;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const k = selKeyRef.current;
      if (!k || !canEdit || (e.key !== 'Delete' && e.key !== 'Backspace') || isTyping(e, 'input, textarea, select, [contenteditable]')) return;
      e.preventDefault();
      e.stopPropagation();
      seal?.();
      apply((d) => ops.removeVolumeKey(d, k.clipId, k.index));
      seal?.();
      setSelKey(null);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [apply, seal, canEdit]);
  useEffect(() => { if (selection.items.length) setSelKey(null); }, [selection]);

  // Track labels follow the lanes' vertical scroll (they used to stay put while the lanes scrolled).
  const onLanesScroll = () => { if (labels.current && lanes.current) labels.current.scrollTop = lanes.current.scrollTop; };

  /* ---------------------------------------------- dragging */

  /** The edges a drag moves (record ticks), for snapping; none for drags that do not snap. */
  const dragEdges = (kind: DragKind, id: string, grabbed?: ItemRef): number[] => {
    const p = placed.find((q) => q.clip.id === id);
    const a = allAudioTracks(doc).flatMap((t) => t.clips).find((c) => c.id === id);
    const l = layers.flatMap((ly) => ly.clips).find((c) => c.id === id);
    const g = doc.graphics.find((c) => c.id === id);
    switch (kind) {
      case 'trim-in': return p ? [p.recordIn] : [];
      case 'trim-out': return p ? [p.recordOut] : [];
      // 1.7.3: a V1 body drag snaps its edges too (it lands freely now).
      case 'move-video': return p ? [p.recordIn, p.recordOut] : [];
      case 'move-items': { const s = grabbed ? itemSpan(doc, grabbed) : null; return s ? [s.start, s.end] : []; }
      case 'move-audio': return a ? [a.recordInTicks, a.recordInTicks + clipDuration(a)] : [];
      case 'trim-audio-in': return a ? [a.recordInTicks] : [];
      case 'trim-audio-out': return a ? [a.recordInTicks + clipDuration(a)] : [];
      case 'move-layer': return l ? [l.recordInTicks, l.recordInTicks + clipDuration(l)] : [];
      case 'trim-layer-in': return l ? [l.recordInTicks] : [];
      case 'trim-layer-out': return l ? [l.recordInTicks + clipDuration(l)] : [];
      case 'move-graphic': return g ? [g.recordInTicks, g.recordInTicks + g.durationTicks] : [];
      case 'resize-graphic': return g ? [g.recordInTicks + g.durationTicks] : [];
      case 'move-marker': { const m = doc.markers?.find((k) => k.id === id); return m ? [m.atTicks] : []; }
      case 'move-cue': { const c = doc.subtitles?.cues.find((k) => k.id === id); return c ? [c.startTicks, c.endTicks] : []; }
      case 'trim-cue-in': { const c = doc.subtitles?.cues.find((k) => k.id === id); return c ? [c.startTicks] : []; }
      case 'trim-cue-out': { const c = doc.subtitles?.cues.find((k) => k.id === id); return c ? [c.endTicks] : []; }
      // 1.7.2: a slide snaps the clip's edges; a slip moves nothing on the timeline, so it snaps nothing.
      case 'slide-video': return p ? [p.recordIn, p.recordOut] : [];
      case 'slide-layer': return l ? [l.recordInTicks, l.recordInTicks + clipDuration(l)] : [];
      case 'slide-audio': return a ? [a.recordInTicks, a.recordInTicks + clipDuration(a)] : [];
      // 1.7.4: a ripple snaps the edge it trims, a roll the cut it moves.
      case 'ripple-in': case 'roll': { const s = grabbed ? itemSpan(doc, grabbed) : null; return s ? [s.start] : []; }
      case 'ripple-out': { const s = grabbed ? itemSpan(doc, grabbed) : null; return s ? [s.end] : []; }
      default: return [];
    }
  };

  /** What dragging a clip's body does (1.7.2): Option (Alt) slips with any tool; otherwise the tool's drag, or `move`. */
  const bodyKind = (e: RPointerEvent, track: TrimTrack, move: DragKind): DragKind => {
    if (!canEdit) return 'press';
    if (e.altKey || tool === 'slip') return `slip-${track}`;
    return tool === 'slide' ? `slide-${track}` : move;
  };
  const durationOf = useCallback((versionId: string) => sourceDurations.get(versionId) ?? 0, [sourceDurations]);
  // 1.8.0: the sources' lengths for crossfades across the cut and transitions that use the clips' extra media.
  const lengths = useMemo(() => sourceLengths(sources?.values() ?? []), [sources]);

  const begin = (e: RPointerEvent, kind: DragKind, id: string, picked?: Pick<DragState, 'grabbed' | 'click' | 'dragSel'>) => {
    e.stopPropagation();
    manual.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    // 1.7.3: a move of an item that is one of several selected moves them all.
    const moving = (picked?.dragSel?.items ?? []).filter((r) => r.kind !== 'gap');
    if (MOVE_KIND[kind] && moving.length > 1) kind = 'move-items';
    const edges = dragEdges(kind, id, picked?.grabbed);
    // Where it may snap: everything but its own edges (so a short drag is not held where it started).
    // 1.7.3: a group move leaves out the edges of everything that moves with it.
    const theirs = kind === 'move-items' ? moving.flatMap((r) => { const s = itemSpan(doc, r); return s ? [s.start, s.end] : []; }) : [];
    candidates.current = snapping ? snapCandidates(doc, clock.get(), id).filter((c) => !edges.includes(c) && !theirs.includes(c)) : [];
    if (kind === 'scrub') candidates.current = snapping ? snapCandidates(doc, -1).filter((c) => c >= 0) : [];
    setDrag({ kind, id, startX: e.clientX, dx: 0, startY: e.clientY, dy: 0, dt: 0, ...picked, moved: false, cmd: e.metaKey || e.ctrlKey });
    if (kind === 'scrub') setPlayhead(scrubTicks(e.clientX));
  };

  /** The pointer's time, snapped to clip edges, the playhead and markers when snapping is on (Blade). */
  const snappedAt = (clientX: number) => {
    const t = pointerTicks(clientX);
    if (!snapping) return t;
    return snapDelta([t], 0, snapCandidates(doc, clock.get()), (SNAP_PX / pps) * S).to ?? t;
  };
  /** Blade (1.7.4): split the item there, or with ⇧ every track there. */
  const blade = (e: RPointerEvent, ref: ItemRef | null) => {
    const t = snappedAt(e.clientX);
    if (e.shiftKey) { apply((d) => ops.splitAll(d, t)); return; }
    if (!ref || ref.kind === 'gap' || ref.kind === 'marker' || ref.kind === 'cue') return;
    apply((d) => ops.splitAt(d, t, { items: [ref] }));
  };
  /** Select forward (1.7.4): everything from the click to the end; with ⇧ that track only; linked items come too (not with ⌥). */
  const forward = (e: RPointerEvent, track: TrackRef | undefined) => {
    const t = pointerTicks(e.clientX);
    const found = selectForward(doc, t, e.shiftKey ? track : undefined);
    const items = uniqueItems(found.flatMap((r) => groupOf(doc, r, e.altKey)));
    setSelection(items.length ? { items, primary: items[0]! } : NO_SELECTION);
  };

  /** Press on an item (1.7.3): select per the modifiers (pressItem), then start `kind` (a drag or a plain press). */
  const press = (e: RPointerEvent, ref: ItemRef, kind: DragKind) => {
    if (e.button !== 0) { e.stopPropagation(); return; }
    if (canEdit && (tool === 'blade' || tool === 'forward') && ref.kind !== 'marker') {
      e.stopPropagation();
      if (tool === 'blade') blade(e, ref); else forward(e, trackOfItem(doc, ref));
      return;
    }
    const p = pressItem(doc, selection, ref, { shift: e.shiftKey, meta: e.metaKey || e.ctrlKey, alt: e.altKey });
    if (p.now !== selection) setSelection(p.now);
    begin(e, canEdit || kind === 'scrub' ? kind : 'press', ref.id, { grabbed: ref, click: p.click, dragSel: p.drag });
  };
  /** Press on an item's edge or handle: it alone is selected (as before 1.7.3), and the handle's drag starts. */
  const pressHandle = (e: RPointerEvent, ref: ItemRef, kind: DragKind) => {
    if (e.button !== 0) { e.stopPropagation(); return; }
    if (!isSelected(selection, ref) || selection.items.length > 1) setSelection(selectOnly(doc, ref, true));
    begin(e, kind, ref.id, { grabbed: ref });
  };
  /**
   * Press on a clip's edge (1.7.4): with the Ripple tool a ripple trim, with Roll the cut at that edge (the incoming
   * clip: this one at its left edge, the one after it at its right edge), with Blade or Select forward what a click
   * on the clip does; otherwise the track's own trim (`kind`).
   */
  const pressEdge = (e: RPointerEvent, ref: ItemRef, kind: DragKind) => {
    if (e.button !== 0) { e.stopPropagation(); return; }
    const left = isInEdge(kind);
    if (canEdit && (tool === 'blade' || tool === 'forward')) { press(e, ref, 'press'); return; }
    if (canEdit && tool === 'ripple') { pressHandle(e, ref, left ? 'ripple-in' : 'ripple-out'); return; }
    if (canEdit && tool === 'roll' && ref.kind !== 'cue') {
      const incoming = left ? (cutBefore(ref) ? ref : null) : cutAfter(ref);
      if (!incoming) { e.stopPropagation(); toast('There is no cut to roll here: the clips on each side must touch', 'error'); return; }
      if (!isSelected(selection, incoming) || selection.items.length > 1) setSelection(selectOnly(doc, incoming, true));
      begin(e, 'roll', incoming.id, { grabbed: incoming });
      return;
    }
    pressHandle(e, ref, kind);
  };
  /** Whether a clip or graphic has a touching item before it on its track (a cut Roll can move). */
  const cutBefore = (ref: ItemRef): boolean => {
    if (ref.kind === 'graphic') { const g = doc.graphics.find((x) => x.id === ref.id); return Boolean(g && doc.graphics.some((o) => o.id !== g.id && graphicTrackOf(o) === graphicTrackOf(g) && o.recordInTicks + o.durationTicks === g.recordInTicks)); }
    return Boolean(outgoingOf(doc, ref.id));
  };
  /** The item after a clip or graphic that touches it (the incoming side of the cut at its right edge). */
  const cutAfter = (ref: ItemRef): ItemRef | null => {
    if (ref.kind === 'graphic') {
      const g = doc.graphics.find((x) => x.id === ref.id);
      const next = g && doc.graphics.find((o) => o.id !== g.id && graphicTrackOf(o) === graphicTrackOf(g) && o.recordInTicks === g.recordInTicks + g.durationTicks);
      return next ? { kind: 'graphic', id: next.id } : null;
    }
    const next = incomingAfter(doc, ref.id);
    return next ? { kind: ref.kind, id: next.id } : null;
  };

  const scrubTicks = (clientX: number) => {
    const t = pointerTicks(clientX);
    if (!candidates.current.length) return t;
    return snapDelta([t], 0, candidates.current, (SNAP_PX / pps) * S).to ?? t;
  };

  /** A point in the lanes' content (the rubber band's coordinates). */
  const contentPoint = (clientX: number, clientY: number) => {
    const r = inner.current!.getBoundingClientRect();
    return { x: clientX - r.left, y: clientY - r.top };
  };
  /** Press on empty lane space (1.7.3): a rubber band; Shift or ⌘ adds to the selection. 1.7.4: Blade ⇧-click splits every track there; Select forward selects from there. */
  const laneDown = (e: RPointerEvent, track?: TrackRef) => {
    if (e.target !== e.currentTarget || e.button !== 0 || !inner.current) return;
    if (canEdit && tool === 'blade') { if (e.shiftKey) blade(e, null); return; }
    if (canEdit && tool === 'forward') { forward(e, track); return; }
    manual.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    const p = contentPoint(e.clientX, e.clientY);
    const add = e.shiftKey || e.metaKey || e.ctrlKey;
    setBand({ x0: p.x, y0: p.y, x1: p.x, y1: p.y, add, base: add ? selection : NO_SELECTION });
  };
  const bandSelect = (b: Band, alt: boolean): TimelineSelection => {
    const box = boxOf({ x: b.x0, y: b.y0 }, { x: b.x1, y: b.y1 });
    const r0 = inner.current!.getBoundingClientRect();
    const rects: Array<{ ref: ItemRef } & Box> = [];
    for (const el of inner.current!.querySelectorAll<HTMLElement>('[data-item]')) {
      const ref = parseItemKey(el.dataset.item);
      if (!ref) continue;
      const r = el.getBoundingClientRect();
      rects.push({ ref, left: r.left - r0.left, right: r.right - r0.left, top: r.top - r0.top, bottom: r.bottom - r0.top });
    }
    const hit = itemsInBox(rects, box).flatMap((r) => groupOf(doc, r, alt));
    return addItems(b.base, hit, hit[0] ?? b.base.primary);
  };

  const move = (e: RPointerEvent) => {
    if (band) {
      const p = contentPoint(e.clientX, e.clientY);
      const next = { ...band, x1: p.x, y1: p.y };
      setBand(next);
      if (!isTinyBox(boxOf({ x: next.x0, y: next.y0 }, { x: next.x1, y: next.y1 }))) setSelection(bandSelect(next, e.altKey));
      return;
    }
    if (!drag) return;
    if (drag.kind === 'scrub') { setPlayhead(scrubTicks(e.clientX)); return; }
    if (drag.kind === 'press') return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    const raw = (dx / pps) * S;
    const edges = dragEdges(drag.kind, drag.id, drag.grabbed);
    const snapped = edges.length && candidates.current.length ? snapDelta(edges, raw, candidates.current, (SNAP_PX / pps) * S).delta : raw;
    const moved = drag.moved || Math.abs(dx) >= 3 || Math.abs(dy) >= LANE / 2;
    // The press became a drag: what moves is the drag's selection (a ⌘-drag of an unselected clip moves it alone).
    if (moved && !drag.moved && drag.dragSel && drag.dragSel !== selection) setSelection(drag.dragSel);
    setDrag({ ...drag, dx, dy, dt: snap(snapped, doc.frameRate), moved, cmd: e.metaKey || e.ctrlKey });
  };

  /** Lanes a vertical drag moves by (down is positive). */
  const laneSteps = (dy: number) => Math.round(dy / LANE);

  /** The most a trim may lengthen an item to (its media's length; 0: no limit). */
  const trimMax = (ref: ItemRef): number => {
    const c = clipOf(doc, ref.id);
    if (!c) return 0;
    const known = sourceDurations.get(c.versionId) ?? 0;
    return ref.kind === 'layer' ? known || Number.MAX_SAFE_INTEGER : known || c.sourceOutTicks;
  };
  /** A move between video tracks (1.7.4): one edit, its notes in a toast, the moved items selected. */
  const trackMove = (items: ItemRef[], target: ReturnType<typeof videoDrop>['target'], d: number, cmd: boolean) => {
    try {
      const r = ops.moveToVideoTrack(doc, items, target, d, { mode: cmd ? 'insert' : 'overwrite', ripple: sync ? 'sync' : 'v1' });
      const note = notesToast(r.notes);
      if (r.doc !== doc) {
        apply(() => r.doc);
        if (r.moved.length) setSelection({ items: r.moved, primary: r.moved[0]! });
      }
      if (note) toast(note, r.doc === doc ? 'error' : undefined);
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err), 'error');
    }
  };

  const end = (e?: RPointerEvent) => {
    if (band) {
      const b = band;
      setBand(null);
      if (isTinyBox(boxOf({ x: b.x0, y: b.y0 }, { x: b.x1, y: b.y1 }))) { if (!b.add) setSelection(NO_SELECTION); }
      else setSelection(bandSelect(b, Boolean(e?.altKey)));
      return;
    }
    if (!drag) return;
    const d = drag.dt;
    const { kind, id } = drag;
    setDrag(null);
    seal?.();
    const still = Math.abs(drag.dx) < 3 && Math.abs(drag.dy) < LANE / 2;
    if (still && !kind.startsWith('trim') && drag.click) setSelection(drag.click);
    if (kind === 'scrub' || kind === 'press' || (still && !kind.startsWith('trim'))) return;
    const ripple = sync ? 'sync' as const : 'v1' as const;
    const steps = laneSteps(drag.dy);
    const mode = drag.cmd ? 'insert' as const : 'overwrite' as const;
    if (kind === 'move-items') {
      const items = (drag.dragSel?.items ?? []).filter((r) => r.kind !== 'gap');
      const g = drag.grabbed;
      const lane = g ? videoLaneOf(doc, g) : null;
      const vd = lane !== null && steps ? videoDrop(doc, lane, -steps) : null;
      const unlinkedLayer = g?.kind === 'layer' && !(clipOf(doc, g.id) as { linkId?: string } | undefined)?.linkId;
      if (vd?.changed && !(unlinkedLayer && vd.lane > 0 && !vd.newTrack)) {
        // 1.7.4: the grabbed picture changes video track: the whole selection goes through moveToVideoTrack.
        trackMove(primaryFirst(items, g), vd.target, d, Boolean(drag.cmd));
      } else if (g?.kind === 'audio' && steps) {
        const ad = audioDrop(doc, audioIndexOf(doc, g.id), steps);
        apply((doc2) => ops.moveItems(doc2, items, d, { mode, ripple, ...(ad.delta ? { audioLaneDelta: ad.delta } : {}), ...(ad.newTrack ? { newTracks: true } : {}) }));
      } else if (g?.kind === 'graphic' && steps) {
        const gd = graphicDrop(doc, graphicTrackOf(doc.graphics.find((x) => x.id === g.id)!), -steps);
        apply((doc2) => ops.moveItems(doc2, items, d, { mode, ripple, ...(gd.delta ? { graphicLaneDelta: gd.delta } : {}), ...(gd.newTrack ? { newTracks: true } : {}) }));
      } else {
        // 1.7.3: unlinked layer clips change layer by laneDelta.
        const laneDelta = vd?.changed && unlinkedLayer ? vd.lane - lane! : 0;
        apply((doc2) => ops.moveItems(doc2, items, d, { mode, laneDelta, ripple }));
      }
    } else if (kind === 'move-video') {
      // 1.7.4: dragged up, a V1 clip goes to a video track (its sound to the audio track as many down).
      const vd = steps < 0 ? videoDrop(doc, 0, -steps) : null;
      if (vd?.changed) trackMove([{ kind: 'video', id }], vd.target, d, Boolean(drag.cmd));
      // 1.7.3: a V1 clip lands freely (overwrite, leaving a gap where it was); ⌘-drag inserts.
      else if (d) apply((doc2) => ops.placeVideo(doc2, [id], d, mode, { ripple }));
    } else if (kind === 'ripple-in' || kind === 'ripple-out') {
      // 1.7.4: the Ripple tool on any track.
      const ref = drag.grabbed;
      if (!d || !ref) return;
      apply((doc2) => ops.rippleTrim(doc2, ref, kind === 'ripple-in' ? 'in' : 'out', d, trimMax(ref), ripple), `ripple:${ref.id}`);
    } else if (kind === 'roll') {
      const ref = drag.grabbed;
      if (!d || !ref) return;
      apply((doc2) => ops.rollEdit(doc2, ref, d, durationOf), `roll:${ref.id}`);
    } else if (drag.cmd && EDGE_TRIMS.has(kind) && kind !== 'trim-in' && kind !== 'trim-out' && drag.grabbed) {
      // 1.7.4: ⌘-drag on an edge off V1 ripples (on V1 it is trimClip, below).
      const ref = drag.grabbed;
      if (!d) return;
      apply((doc2) => ops.rippleTrim(doc2, ref, isInEdge(kind) ? 'in' : 'out', d, trimMax(ref), ripple), `ripple:${ref.id}`);
    } else if (kind === 'trim-in' || kind === 'trim-out') {
      if (!d) return;
      const clip = doc.video.find((c) => c.id === id)!;
      const max = sourceDurations.get(clip.versionId) ?? clip.sourceOutTicks;
      const edge = kind === 'trim-in' ? 'in' : 'out';
      // 1.7.3: the Select tool trims without rippling (a gap, or using one up); ⌘-drag ripples as before.
      apply((doc2) => (drag.cmd ? ops.trimClip(doc2, id, edge, d, max) : ops.trimClipNoRipple(doc2, id, edge, d, max)));
    } else if (kind === 'move-audio') {
      const trackId = audioTrackOf(doc, id);
      const from = audioTracks.findIndex((t) => t.id === trackId);
      const c = audioTracks[from]?.clips.find((a) => a.id === id);
      if (!c) return;
      // 1.7.4: below the last audio track a new one is made (up to A8).
      const ad = audioDrop(doc, from, steps);
      if (ad.newTrack) { apply((doc2) => ops.moveItems(doc2, [{ kind: 'audio', id }], d, { audioLaneDelta: ad.delta, newTracks: true })); return; }
      // 1.7.3: down a lane is the next audio track (A2, A3 …).
      const to = audioTracks[Math.max(0, Math.min(audioTracks.length - 1, from + steps))]!;
      apply((doc2) => ops.moveAudio(doc2, id, c.recordInTicks + d, to.id === trackId ? undefined : to.id));
    } else if (kind === 'trim-audio-in' || kind === 'trim-audio-out') {
      if (!d) return;
      const c = audioTracks.flatMap((t) => t.clips).find((q) => q.id === id);
      if (!c) return;
      const max = sourceDurations.get(c.versionId) || c.sourceOutTicks;
      apply((doc2) => ops.trimAudio(doc2, id, kind === 'trim-audio-in' ? 'in' : 'out', d, max));
    } else if (kind === 'move-graphic') {
      const g = doc.graphics.find((a) => a.id === id)!;
      // 1.7.4: above the top graphics track a new one is made (up to GFX 3).
      const gd = graphicDrop(doc, graphicTrackOf(g), -steps);
      if (gd.newTrack) { apply((doc2) => ops.moveItems(doc2, [{ kind: 'graphic', id }], d, { graphicLaneDelta: gd.delta, newTracks: true })); return; }
      // 1.7.3: up a lane is the graphics track above (GFX 1 is the lowest lane).
      const track = Math.max(0, Math.min(gfxCount - 1, graphicTrackOf(g) - steps)) as GraphicTrack;
      apply((doc2) => ops.moveGraphic(doc2, id, g.recordInTicks + d, track));
    } else if (kind === 'resize-graphic') {
      const g = doc.graphics.find((a) => a.id === id)!;
      apply((doc2) => ops.updateGraphic(doc2, id, { durationTicks: g.durationTicks + d }));
    } else if (kind === 'move-step') {
      // An OGraf step mark: `<graphic id>:<index>`, kept inside the clip.
      const [gid, k] = [id.slice(0, id.lastIndexOf(':')), Number(id.slice(id.lastIndexOf(':') + 1))];
      const g = doc.graphics.find((a) => a.id === gid);
      if (!g?.stepMarks || !d) return;
      const marks = g.stepMarks.map((m, i) => (i === k ? Math.min(g.durationTicks - tpf, Math.max(tpf, m + d)) : m));
      apply((doc2) => ops.setStepMarks(doc2, gid, marks));
    } else if (kind === 'transition-in' || kind === 'transition-out') {
      // Dragging either edge outwards lengthens the transition; it stays centred on the overlap.
      const p = placed.find((q) => q.clip.id === id);
      if (!p?.transition || !d) return;
      const ticks = p.transition.ticks + (kind === 'transition-out' ? d : -d);
      apply((doc2) => ops.setTransition(doc2, id, { kind: p.transition!.kind, durationTicks: Math.max(tpf, ticks) }, lengths));
    } else if (kind === 'move-layer') {
      const li = layers.findIndex((l) => l.clips.some((c) => c.id === id));
      const c = layers[li]!.clips.find((q) => q.id === id)!;
      // 1.7.4: down past V2 is V1, above the top track a new video track; a linked clip takes its sound along.
      const vd = videoDrop(doc, li + 1, -steps);
      if (vd.changed && (vd.lane === 0 || vd.newTrack || c.linkId)) { trackMove([{ kind: 'layer', id }], vd.target, d, Boolean(drag.cmd)); return; }
      // Lanes show the top layer first, so dragging up moves to a higher layer.
      const to = Math.max(0, Math.min(layers.length - 1, li - steps));
      apply((doc2) => ops.moveLayerClip(doc2, id, c.recordInTicks + d, layers[to]!.id));
    } else if (kind === 'trim-layer-in' || kind === 'trim-layer-out') {
      if (!d) return;
      const c = layers.flatMap((l) => l.clips).find((q) => q.id === id)!;
      // Stills have no length of their own: they can be stretched freely.
      const max = sourceDurations.get(c.versionId) || Number.MAX_SAFE_INTEGER;
      apply((doc2) => ops.trimLayerClip(doc2, id, kind === 'trim-layer-in' ? 'in' : 'out', d, max));
    } else if (kind === 'fade-in' || kind === 'fade-out') {
      // The fade handle sets the picture and the sound fade together (audio clips only have sound).
      const len = fadeLength(kind, id, d);
      if (len === null) return;
      const isAudio = audioTrackOf(doc, id) !== null;
      const patch = kind === 'fade-in'
        ? { fadeInTicks: len, ...(isAudio ? {} : { videoFadeInTicks: len }) }
        : { fadeOutTicks: len, ...(isAudio ? {} : { videoFadeOutTicks: len }) };
      apply((doc2) => ops.updateClip(doc2, id, patch));
    } else if (kind === 'move-marker') {
      const m = doc.markers?.find((k) => k.id === id);
      if (m && d) apply((doc2) => ops.updateMarker(doc2, id, { atTicks: m.atTicks + d }));
    } else if (kind === 'move-cue' && doc.subtitles) {
      apply((doc2) => ops.setSubtitles(doc2, {
        ...doc2.subtitles!,
        cues: doc2.subtitles!.cues.map((c) => (c.id === id ? { ...c, startTicks: Math.max(0, c.startTicks + d), endTicks: Math.max(0, c.endTicks + d) } : c)),
      }));
    } else if (kind === 'trim-cue-in' || kind === 'trim-cue-out') {
      // 1.7.1: a cue's edge, in whole frames, kept off its neighbours (ops.trimCue).
      if (d) apply((doc2) => ops.trimCue(doc2, id, kind === 'trim-cue-in' ? 'in' : 'out', d, tpf));
    } else {
      // 1.7.2: a slip or a slide, one edit (one undo step).
      const mode = trimMode(kind);
      if (mode && d) apply((doc2) => trimEdit(doc2, mode, id, d, durationOf));
    }
  };

  /* ---------------------------------------------- slip and slide preview (1.7.2) */

  const trimDragMode = drag ? trimMode(drag.kind) ?? (drag.kind === 'roll' ? 'roll' : null) : null;
  const trimDragId = trimDragMode ? drag!.id : null;
  const trimDragDt = trimDragMode ? drag!.dt : 0;
  // 1.7.4: a ripple drag previews the trim and what moves after it.
  const rippleKind = drag && (drag.kind === 'ripple-in' || drag.kind === 'ripple-out' || (drag.cmd && EDGE_TRIMS.has(drag.kind) && drag.kind !== 'trim-in' && drag.kind !== 'trim-out')) ? drag.kind : null;
  const rippleRef = rippleKind ? drag!.grabbed ?? null : null;
  const rippleDt = rippleKind ? drag!.dt : 0;
  const rollRef = drag?.kind === 'roll' ? drag.grabbed ?? null : null;
  // The document as the slip, slide, roll or ripple would leave it, drawn on the lanes while dragging.
  const previewDoc = useMemo(() => {
    if (rippleKind && rippleRef && rippleDt) return attempt(() => ops.rippleTrim(doc, rippleRef, isInEdge(rippleKind) ? 'in' : 'out', rippleDt, trimMax(rippleRef), sync ? 'sync' : 'v1'));
    if (trimDragMode === 'roll' && rollRef && trimDragDt) return attempt(() => ops.rollEdit(doc, rollRef, trimDragDt, durationOf));
    return trimDragMode && trimDragMode !== 'roll' && trimDragId && trimDragDt ? trimEdit(doc, trimDragMode, trimDragId, trimDragDt, durationOf) : null;
  }, [doc, trimDragMode, trimDragId, trimDragDt, durationOf, rippleKind, rippleRef, rippleDt, rollRef, sync]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!trimPreview) return;
    trimPreview.set(trimDragMode && trimDragId ? { mode: trimDragMode, clipId: trimDragId, before: doc, after: previewDoc ?? doc } : null);
  }, [trimPreview, trimDragMode, trimDragId, previewDoc, doc]);
  useEffect(() => () => trimPreview?.set(null), [trimPreview]);
  const view = previewDoc ?? doc;
  const viewPlaced = useMemo(() => (previewDoc ? layoutVideo(previewDoc) : placed), [previewDoc, placed]);
  const viewFades = useMemo(() => (previewDoc ? storylinePictureFades(previewDoc, viewPlaced) : vfades), [previewDoc, viewPlaced, vfades]);
  const viewLayers = view.layers ?? [];
  const viewAudio = useMemo(() => (previewDoc ? allAudioTracks(previewDoc) : audioTracks), [previewDoc, audioTracks]);
  const viewGaps = useMemo(() => gapsOf(view), [view]);
  /** The slip readout on the clip being slipped: the offset and the new source in point. */
  const slipReadout = (id: string, sourceInTicks: number) => {
    if (trimDragMode !== 'slip' || trimDragId !== id) return null;
    const was = clipOf(doc, id);
    return <span className="tl-slip-readout mono" data-testid="slip-readout">{signedTimecode(sourceInTicks - (was?.sourceInTicks ?? sourceInTicks), doc.frameRate)} · in {tc(sourceInTicks)}</span>;
  };

  /** A clip's fade handle length (the picture fade, or the sound fade on an audio track) with a drag of `d` ticks applied. */
  const fadeLength = (kind: 'fade-in' | 'fade-out', id: string, d: number): number | null => {
    const c = clipOf(doc, id);
    if (!c) return null;
    const isAudio = audioTrackOf(doc, id) !== null;
    const base = kind === 'fade-in' ? (isAudio ? c.fadeInTicks : c.videoFadeInTicks ?? 0) : (isAudio ? c.fadeOutTicks : c.videoFadeOutTicks ?? 0);
    return Math.max(0, Math.min(clipDuration(c), base + (kind === 'fade-in' ? d : -d)));
  };

  // While a ripple or roll previews the result, the dragged edge is drawn from that document, not offset.
  const dtOf = (kind: DragKind, id: string) => (drag && drag.kind === kind && drag.id === id && !(rippleKind && previewDoc) ? drag.dt : 0);
  const offset = (kind: DragKind, id: string) => x(dtOf(kind, id));
  /** 1.7.3: whether this item is in a multi-item move in progress. */
  const inGroupMove = (ref: ItemRef) => Boolean(drag?.kind === 'move-items' && drag.dragSel?.items.some((r) => r.kind === ref.kind && r.id === ref.id));
  /** How far a moving item is drawn from its place (pixels): its own move, or the group's. */
  const moveOffset = (ref: ItemRef, kind: DragKind) => (inGroupMove(ref) ? x(drag!.dt) : offset(kind, ref.id));
  const groupLaneDelta = drag?.kind === 'move-items' && drag.grabbed?.kind === 'layer' ? laneSteps(drag.dy) : 0;
  /**
   * 1.7.4: how many video tracks a dragged V1 clip goes up (its A1 is drawn as many audio tracks down), and which
   * kind of track a drop would make (the dashed "New track" lane).
   */
  const vertical = useMemo(() => {
    if (!drag?.moved) return { v1Up: 0, newLane: null as 'video' | 'audio' | 'graphic' | null };
    const steps = laneSteps(drag.dy);
    const g = drag.grabbed;
    const kind = drag.kind === 'move-items' ? g?.kind : MOVE_KIND[drag.kind];
    if (!g || !steps || !kind) return { v1Up: 0, newLane: null };
    if (kind === 'video' || kind === 'layer') {
      const lane = videoLaneOf(doc, g);
      if (lane === null) return { v1Up: 0, newLane: null };
      const vd = videoDrop(doc, lane, -steps);
      return { v1Up: kind === 'video' ? vd.lane : 0, newLane: vd.newTrack ? 'video' : null };
    }
    if (kind === 'audio') return { v1Up: 0, newLane: audioDrop(doc, audioIndexOf(doc, g.id), steps).newTrack ? 'audio' : null };
    if (kind === 'graphic') {
      const gr = doc.graphics.find((x) => x.id === g.id);
      return { v1Up: 0, newLane: gr && graphicDrop(doc, graphicTrackOf(gr), -steps).newTrack ? 'graphic' : null };
    }
    return { v1Up: 0, newLane: null };
  }, [drag, doc]);
  /** How far up (px) a V1 clip moving up `n` video tracks is drawn: out of the tall V1 lane, then a lane a track. */
  const v1Lift = (n: number) => (n ? (52 - LANE) / 2 + n * LANE : 0);
  const v1Moving = (ref: ItemRef) => vertical.v1Up > 0 && ((drag?.kind === 'move-video' && drag.id === ref.id) || inGroupMove(ref));
  const newTrackLane = (kind: 'video' | 'audio' | 'graphic', below = false) => (vertical.newLane === kind
    ? <div className={`tl-new-track ${below ? 'below' : 'above'}`} data-testid="new-track-lane" data-kind={kind}>New track</div>
    : null);
  const step = pps >= 120 ? 1 : pps >= 50 ? 2 : pps >= 20 ? 5 : pps >= 8 ? 10 : pps >= 3 ? 30 : 60;
  const marks = Array.from({ length: Math.ceil(width / pps / step) + 1 }, (_, i) => i * step);
  // 1.7.2: a ruler timecode under a marker's label is left out (the label is drawn instead).
  const coveredMarks = rulerTicksUnderMarkers(marks.map((m) => m * pps), (doc.markers ?? []).map((m) => ({ x: x(m.atTicks) + moveOffset({ kind: 'marker', id: m.id }, 'move-marker'), label: m.label })));
  /** A trim handle's class (1.7.2): `active` while its own drag is on, so its edge bracket stays lit off the handle. */
  const handle = (side: 'left' | 'right', kind: DragKind, id: string) => `tl-handle ${side}${drag?.kind === kind && drag.id === id ? ' active' : ''}`;

  /** Fade handles at a clip's top corners (0.96.0): drag to set the fade length. `len` is what is drawn now. */
  const fadeHandles = (ref: ItemRef, inLen: number, outLen: number) => (
    canEdit ? (
      <>
        <span className="tl-fade-handle in" data-testid="fade-handle-in" style={{ left: Math.max(0, x(inLen + (drag?.kind === 'fade-in' && drag.id === ref.id ? drag.dt : 0))) }}
          title="Drag to fade in (picture and sound)" onPointerDown={(e) => pressHandle(e, ref, 'fade-in')} />
        <span className="tl-fade-handle out" data-testid="fade-handle-out" style={{ right: Math.max(0, x(outLen - (drag?.kind === 'fade-out' && drag.id === ref.id ? drag.dt : 0))) }}
          title="Drag to fade out (picture and sound)" onPointerDown={(e) => pressHandle(e, ref, 'fade-out')} />
      </>
    ) : null
  );
  /** The fade as drawn while dragging its handle. */
  const liveFade = (kind: 'fade-in' | 'fade-out', id: string, len: number) => (drag?.kind === kind && drag.id === id ? fadeLength(kind, id, drag.dt) ?? len : len);

  /** 1.7.4: the track's gain (dB) in its header, one undo step per change. */
  const trackGain = (track: string, name: string) => {
    const g = trackGainDb(doc, track);
    return (
      <input type="number" className={`tl-gain mono ${g ? 'set' : ''}`} step={0.5} min={-60} max={12} value={g} disabled={!canEdit}
        aria-label={`${name} gain (dB)`} title={`${name} volume: added to every clip's level on the track (preview and export)`} data-testid={`track-gain-${track}`}
        onChange={(e) => { const v = Number(e.target.value); if (Number.isFinite(v)) apply((d) => ops.setTrackGain(d, track, v), `trackgain:${track}`); }}
        onBlur={() => seal?.()} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
    );
  };
  /** 1.7.4: Source V / Source A, where Insert and Overwrite put the source's picture and sound (one of each, or none). */
  const videoTarget = (v: 'v1' | { layer: string }, name: string, testId: string) => {
    if (!targets || !setTargets) return null;
    const on = v === 'v1' ? targets.video === 'v1' : typeof targets.video === 'object' && targets.video?.layer === v.layer;
    return (
      <button type="button" className={`tl-target video ${on ? 'on' : ''}`} aria-pressed={on} aria-label={`Source video to ${name}`} data-testid={testId}
        title={on ? `Insert and Overwrite put the source's picture on ${name} (click: no picture)` : `Put the source's picture on ${name} with Insert and Overwrite`}
        onClick={() => setTargets({ ...targets, video: on ? null : v })}>V</button>
    );
  };
  const audioTarget = (a: 'with-picture' | { track: string }, name: string, testId: string) => {
    if (!targets || !setTargets) return null;
    const on = a === 'with-picture' ? targets.audio === 'with-picture' : typeof targets.audio === 'object' && targets.audio?.track === a.track;
    return (
      <button type="button" className={`tl-target audio ${on ? 'on' : ''}`} aria-pressed={on} aria-label={`Source sound to ${name}`} data-testid={testId}
        title={on ? `Insert and Overwrite put the source's sound on ${name} (click: no sound)` : `Put the source's sound on ${name} with Insert and Overwrite`}
        onClick={() => setTargets({ ...targets, audio: on ? null : a })}>A</button>
    );
  };

  /** Mute and solo of a sound track (0.96.0). */
  const trackButtons = (track: string, name: string) => {
    const st = doc.trackAudio?.[track];
    const heard = trackAudible(doc, track);
    return (
      <span className="tl-track-audio">
        <button className={`tl-ms ${st?.mute ? 'on mute' : ''}`} aria-pressed={Boolean(st?.mute)} aria-label={`Mute ${name}`} title={`Mute ${name} (preview and export)`} disabled={!canEdit}
          data-testid={`track-mute-${name.toLowerCase()}`} onClick={() => apply((d) => ops.setTrackAudio(d, track, { mute: !st?.mute }))}>M</button>
        <button className={`tl-ms ${st?.solo ? 'on solo' : ''}`} aria-pressed={Boolean(st?.solo)} aria-label={`Solo ${name}`} title={`Solo ${name}: only soloed tracks are heard`} disabled={!canEdit}
          data-testid={`track-solo-${name.toLowerCase()}`} onClick={() => apply((d) => ops.setTrackAudio(d, track, { solo: !st?.solo }))}>S</button>
        {trackGain(track, name)}
        {!heard && <span className="sr-only">silent</span>}
      </span>
    );
  };

  /* ---------------------------------------------- the drop preview of a V1 move (1.7.3) */

  /** Where moved V1 clips land: the range they cover after the drag, and whether it inserts. */
  const v1Drop = useMemo(() => {
    if (!drag || !drag.moved || (drag.kind !== 'move-video' && drag.kind !== 'move-items')) return null;
    // 1.7.4: a V1 clip dragged up to a video track does not land on V1.
    if ((drag.grabbed?.kind === 'video' || drag.kind === 'move-video') && laneSteps(drag.dy) < 0 && videoDrop(doc, 0, -laneSteps(drag.dy)).changed) return null;
    const ids = drag.kind === 'move-video' ? [drag.id] : (drag.dragSel?.items ?? []).filter((r) => r.kind === 'video').map((r) => r.id);
    const ps = placed.filter((p) => ids.includes(p.clip.id));
    if (!ps.length) return null;
    const start = Math.min(...ps.map((p) => p.recordIn));
    const end = Math.max(...ps.map((p) => p.recordOut));
    const dt = Math.max(-start, drag.dt);
    return { start: start + dt, end: end + dt, insert: Boolean(drag.cmd) };
  }, [drag, placed, doc]);

  /* ---------------------------------------------- the graphics tracks (1.7.3) */

  const gfxTracks = Array.from({ length: gfxCount }, (_, i) => i as GraphicTrack);
  const toggleGfx = (t: number) => setHiddenGfx?.(hiddenGfx.includes(t) ? hiddenGfx.filter((h) => h !== t) : [...hiddenGfx, t].sort());
  const removeGfx = (t: GraphicTrack) => {
    const n = doc.graphics.filter((g) => graphicTrackOf(g) === t).length;
    if (n && !window.confirm(`Remove GFX ${t + 1} and its ${n} graphic${n === 1 ? '' : 's'}?`)) return;
    apply((d) => ops.removeGraphicTrack(d, t));
    // The tracks above move down: so do their hidden marks.
    if (setHiddenGfx && hiddenGfx.length) setHiddenGfx(hiddenGfx.filter((h) => h !== t).map((h) => (h > t ? h - 1 : h)));
  };
  const removeAudioTrack = (id: string, name: string, clips: number) => {
    if (clips && !window.confirm(`Remove ${name} and its ${clips} clip${clips === 1 ? '' : 's'}?`)) return;
    apply((d) => ops.removeAudioTrack(d, id));
  };

  /** A graphics lane (GFX 1–3): its graphics, a drop target for new ones. */
  const graphicsLane = (track: GraphicTrack) => (
    <div key={`gfx-${track}`} className={`tl-lane gfx-lane ${dropOver === track ? 'drop-target' : ''} ${hiddenGfx.includes(track) ? 'hidden' : ''}`}
      data-testid={track === 0 ? 'graphics-lane' : `graphics-lane-${track + 1}`} data-gfx={track + 1}
      onPointerDown={(e) => laneDown(e, { kind: 'graphic', track })} onContextMenu={(e) => { if (e.target === e.currentTarget) onItemMenu(e, null); }}
      onDragOver={(e) => { if (!canEdit || !onDropGraphic || !e.dataTransfer.types.includes(GRAPHIC_DRAG_TYPE)) return; e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setDropOver(track); }}
      onDragLeave={() => setDropOver(null)}
      onDrop={(e) => {
        setDropOver(null);
        const dropped = readGraphicDrag(e);
        if (!dropped || !onDropGraphic || !canEdit) return;
        e.preventDefault();
        onDropGraphic(dropped, pointerTicks(e.clientX), track);
      }}>
      {track === gfxCount - 1 && newTrackLane('graphic')}
      {view.graphics.filter((g) => graphicTrackOf(g) === track).map((g) => {
        const ref: ItemRef = { kind: 'graphic', id: g.id };
        const dx = moveOffset(ref, 'move-graphic');
        const dw = offset('resize-graphic', g.id);
        const t = templateFor(templates, g);
        const ograf = isOgrafTemplate(t) ? t.ograf : null;
        // A sting (OGraf stepCount 0) plays for its own length: it is not resized.
        const fixed = Boolean(ograf && ografIsSting(ograf));
        const newer = newerVersion(templates, g);
        const label = ograf ? ografLabel(ograf, g.data) : String(g.data.name || Object.values(g.data)[0] || (g.templateId.startsWith('asset-') ? 'Graphic' : g.templateId));
        // A graphic dragged up or down goes to another graphics track (drawn there while dragging).
        const moving = drag?.kind === 'move-graphic' && drag.id === g.id && drag.moved;
        const toTrack = moving ? Math.max(0, Math.min(Math.min(gfxCount, MAX_GRAPHIC_TRACKS - 1), track - laneSteps(drag.dy))) : track;
        return (
          <div key={g.id} className={`tl-clip gfx ${sel('graphic', g.id) ? 'selected' : ''} ${moving ? 'dragging' : ''}`} data-clip={g.id} data-item={itemKey(ref)} data-label={label}
            style={{ left: x(g.recordInTicks) + dx, width: Math.max(6, x(g.durationTicks) + dw), transform: toTrack !== track ? `translateY(${(track - toTrack) * LANE}px)` : undefined }}
            title={`${label}${fixed ? ' · a sting: its length is set by the graphic' : ''}${newer ? ` · version ${newer.version} is available` : ''}`}
            onPointerDown={(e) => press(e, ref, 'move-graphic')} onContextMenu={(e) => onItemMenu(e, ref)}>
            <span className="tl-clip-label"><LinkMark linkId={g.linkId} />{newer && <span className="tl-newer" data-testid="newer-version" aria-label={`Version ${newer.version} is available`}>v{newer.version}↑</span>}{label}</span>
            {(g.updates ?? []).map((u, k) => <span key={`u${k}`} className="tl-update-mark" style={{ left: x(u.atTicks) }} title={`Update at ${tc(g.recordInTicks + u.atTicks)}`} />)}
            {(g.stepMarks ?? []).map((m, k) => (
              <span key={`s${k}`} className="tl-step-mark" data-testid="step-mark" style={{ left: x(m) + offset('move-step', `${g.id}:${k}`) }}
                title={`Next step at ${tc(g.recordInTicks + m)}${canEdit ? ' · drag to move, double-click to delete' : ''}`}
                onPointerDown={(e) => { if (e.button !== 0) return; setSelection(selectOnly(doc, ref, true)); if (canEdit) begin(e, 'move-step', `${g.id}:${k}`); else e.stopPropagation(); }}
                onDoubleClick={(e) => { e.stopPropagation(); if (canEdit) apply((d2) => ops.setStepMarks(d2, g.id, g.stepMarks!.filter((_, i) => i !== k))); }} />
            ))}
            {!fixed && canEdit && <span className={handle('right', 'resize-graphic', g.id)} onPointerDown={(e) => pressEdge(e, ref, 'resize-graphic')} />}
          </div>
        );
      })}
    </div>
  );

  const menuEntries = menu && menuFor ? menuFor(menu.sel) : [];

  /* ---------------------------------------------- the sequence In and Out on the ruler (1.7.4) */

  const marksNow = doc.marks;
  const markAt = (which: 'in' | 'out') => (markDrag?.which === which ? markDrag.to : which === 'in' ? marksNow?.inTicks : marksNow?.outTicks);
  const pressMark = (e: RPointerEvent, which: 'in' | 'out') => {
    e.stopPropagation();
    if (e.button !== 0 || !canEdit) return;
    const from = markAt(which);
    if (from === undefined) return;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    setMarkDrag({ which, startX: e.clientX, from, to: from });
  };
  const moveMark = (e: RPointerEvent) => {
    if (!markDrag) return;
    e.stopPropagation();
    setMarkDrag({ ...markDrag, to: Math.max(0, snap(markDrag.from + ((e.clientX - markDrag.startX) / pps) * S, doc.frameRate)) });
  };
  const endMark = (e: RPointerEvent) => {
    if (!markDrag) return;
    e.stopPropagation();
    const m = markDrag;
    setMarkDrag(null);
    if (m.to !== m.from) apply((d) => ops.setMarks(d, m.which === 'in' ? { inTicks: m.to } : { outTicks: m.to }));
    seal?.();
  };
  const seqMarks = (() => {
    const inT = markAt('in');
    const outT = markAt('out');
    if (inT === undefined && outT === undefined) return null;
    const a = inT ?? 0;
    const b = outT ?? Math.max(a, total);
    return (
      <div className="tl-seq-marks" data-testid="seq-marks" data-in={inT ?? ''} data-out={outT ?? ''} style={{ left: x(a), width: Math.max(0, x(b - a)) }}
        title={`Sequence In ${inT === undefined ? '(start)' : tc(inT)} – Out ${outT === undefined ? '(end)' : tc(outT)}`}>
        {inT !== undefined && <span className="tl-seq-mark in" data-testid="seq-mark-in" title={canEdit ? 'Sequence In · drag to move (I sets it, ⌥X clears)' : 'Sequence In'}
          onPointerDown={(e) => pressMark(e, 'in')} onPointerMove={moveMark} onPointerUp={endMark} onPointerCancel={endMark} />}
        {outT !== undefined && <span className="tl-seq-mark out" data-testid="seq-mark-out" title={canEdit ? 'Sequence Out · drag to move (O sets it, ⌥X clears)' : 'Sequence Out'}
          onPointerDown={(e) => pressMark(e, 'out')} onPointerMove={moveMark} onPointerUp={endMark} onPointerCancel={endMark} />}
      </div>
    );
  })();

  /* ---------------------------------------------- levels and crossfades (1.7.4) */

  const xfades = useMemo(() => crossfadeMarks(view, lengths), [view, lengths]); // eslint-disable-line react-hooks/exhaustive-deps
  /** The level line of a sound clip, on `track` (its gain is in the readout). 1.8.0: none for a silent source. */
  const levelLine = (clip: Parameters<typeof LevelLine>[0]['clip'] & { versionId: string }, track: string) => (isSilent(sources, clip.versionId) ? null : (
    <LevelLine clip={clip} trackGain={trackGainDb(doc, track)} pps={pps} rate={doc.frameRate} canEdit={canEdit} apply={apply} seal={seal}
      selectedKey={selKey?.clipId === clip.id ? selKey.index : null}
      onSelectKey={(i) => { setSelKey(i === null ? null : { clipId: clip.id, index: i }); if (i !== null && selection.items.length) setSelection(NO_SELECTION); }} />
  ));
  /**
   * A crossfade at a cut on this lane ('a1' or an audio track), over its real blend (1.8.0: across the cut when
   * centred, after it when it starts there), with a handle on each edge that can move: dragging one changes the
   * length, both sides together for a centred one; one undo step per drag.
   */
  const crossfadesOn = (lane: 'a1' | string) => xfades.filter((f) => (lane === 'a1' ? f.kind === 'video' : f.kind === 'audio' && audioTrackOf(doc, f.id) === lane)).map((f) => {
    const edge = (side: 'left' | 'right') => (
      <span className="tl-crossfade-edge" data-testid={`crossfade-edge-${side}`} style={side === 'right' ? { left: 'auto', right: -3 } : undefined} onPointerDown={(e) => {
        if (e.button !== 0) return;
        e.stopPropagation();
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        const startX = e.clientX;
        const base = f.ticks;
        const ref: ItemRef = { kind: f.kind, id: f.id };
        const el = e.target as HTMLElement;
        const by = (ev: PointerEvent) => ((ev.clientX - startX) / ppsRef.current) * S * (side === 'left' ? -1 : 1) * (f.align === 'center' ? 2 : 1);
        const onMove = (ev: PointerEvent) => apply((d) => ops.setCrossfade(d, ref, Math.max(tpf, base + by(ev)), f.align, lengths), `xfade:${f.id}`);
        const onUp = () => { el.removeEventListener('pointermove', onMove); el.removeEventListener('pointerup', onUp); el.removeEventListener('pointercancel', onUp); seal?.(); };
        el.addEventListener('pointermove', onMove);
        el.addEventListener('pointerup', onUp);
        el.addEventListener('pointercancel', onUp);
      }} />
    );
    return (
      <span key={`xf-${f.id}`} className="tl-crossfade" data-testid="crossfade" data-ticks={f.ticks} data-clip={f.id} data-align={f.align}
        style={{ left: x(f.fromTicks), width: Math.max(4, x(f.ticks)) }} title={`Crossfade ${(f.ticks / S).toFixed(2)} s · ${CROSSFADE_ALIGN_LABELS[f.align]}${canEdit ? ' · drag an edge to change it' : ''}`}>
        {canEdit && f.align !== 'start' && edge('left')}
        {canEdit && f.align !== 'end' && edge('right')}
      </span>
    );
  });

  return (
    <div className="timeline-editor" data-testid="timeline-editor">
      <div className="tl-toolbar">
        <LiveTimecode clock={clock} rate={doc.frameRate} total={total} onJump={(t) => setPlayhead(t)} />
        <span className="faint mono">/ {tc(total)}</span>
        <span style={{ flex: 1 }} />
        {canEdit && (
          <span className="tl-transition-tool">
            <button className="btn ghost sm" disabled={placed.length < 2} onClick={() => onAddTransition(transitionKind)} data-testid="add-transition"
              title="Add the chosen transition at the selected clip's start, or at the cut nearest the playhead (T)" aria-label="Transition"><Blend size={13} /><span className="tl-wide"> Transition</span></button>
            <select className="select sm" value={transitionKind} onChange={(e) => { setTransitionKind(e.target.value as TransitionKind); }} aria-label="Transition type">
              {TRANSITION_KINDS.map((k) => <option key={k} value={k}>{TRANSITION_LABELS[k]}</option>)}
            </select>
          </span>
        )}
        {canEdit && <button className="btn ghost sm" disabled={layers.length >= MAX_LAYERS} onClick={() => apply((d) => ops.addLayer(d))} data-testid="add-layer" title="Add a video track above the others (up to V9)" aria-label="Video track"><Plus size={13} /><span className="tl-wide"> Video track</span><span className="tl-narrow">V</span></button>}
        {canEdit && <button className="btn ghost sm" disabled={(doc.audioTracks ?? []).length >= MAX_AUDIO_TRACKS} onClick={() => apply((d) => ops.addAudioTrack(d))} data-testid="add-audio-track"
          title="Add an audio track (up to A8)" aria-label="Audio track"><Plus size={13} /><span className="tl-wide"> Audio track</span><span className="tl-narrow">A</span></button>}
        {canEdit && <button className="btn ghost sm" disabled={gfxCount >= MAX_GRAPHIC_TRACKS} onClick={() => apply((d) => ops.addGraphicTrack(d))} data-testid="add-graphics-track"
          title="Add a graphics track above the others (up to GFX 3; higher tracks are drawn on top)" aria-label="Graphics track"><Plus size={13} /><span className="tl-wide"> Graphics track</span><span className="tl-narrow">GFX</span></button>}
        {canEdit && setTool && (
          <span className="tl-tools" role="group" aria-label="Timeline tool">
            {TOOLS.map(({ id, label, key, title, Icon }) => (
              <button key={id} className={`btn ghost icon sm ${tool === id ? 'on' : ''}`} aria-pressed={tool === id} aria-label={label} title={`${title} (${key})`}
                onClick={() => setTool(id)} data-testid={`tool-${id}`}><Icon size={14} /></button>
            ))}
          </span>
        )}
        {canEdit && setSync && (
          <button className={`btn ghost sm tl-sync ${sync ? 'on' : ''}`} aria-pressed={sync} aria-label="Sync" data-testid="sync-ripple"
            title="Sync: a ripple (Shift+Delete, ⌘-drag insert, ⇧⌘V, closing a gap) also moves the clips on the other tracks, so music, B-roll and subtitles stay with the picture"
            onClick={() => setSync(!sync)}><Link2 size={13} /> Sync</button>
        )}
        <button className={`btn ghost icon sm ${snapping ? 'on' : ''}`} aria-pressed={snapping} aria-label="Snapping" title="Snap to clip edges, the playhead and markers (N)" onClick={() => setSnapping(!snapping)} data-testid="snap-toggle"><Magnet size={14} /></button>
        <button className={`btn ghost icon sm ${follow ? 'on' : ''}`} aria-pressed={follow} aria-label="Follow the playhead" title="Follow the playhead during playback (scrolling or dragging stops it until the next play)" onClick={() => setFollow(!follow)} data-testid="follow-toggle"><MoveRight size={14} /></button>
        <button className="btn ghost icon sm" aria-label="Zoom to fit" title="Zoom to fit the whole sequence (\)" onClick={zoomToFit} data-testid="zoom-fit"><Maximize size={14} /></button>
        <label className="faint tl-zoom">
          <span className="tl-wide">Zoom</span> <input type="range" min={MIN_PPS} max={MAX_PPS} value={Math.round(pps)} onChange={(e) => zoomTo(Number(e.target.value))} aria-label="Timeline zoom" />
        </label>
      </div>
      <div className="tl-body">
        <div className="tl-labels" ref={labels}>
          <div className="tl-ruler-label" />
          {reframe && <ReframeLaneLabel doc={doc} formatId={formatView.active} color={formatColor(formatView.activeFormat.index)} canEdit={canEdit}
            onAdd={reframeAdd} onPrev={() => reframeGo(-1)} onNext={() => reframeGo(1)} />}
          {[...gfxTracks].reverse().map((t) => {
            const hidden = hiddenGfx.includes(t);
            const name = gfxCount > 1 ? `GFX ${t + 1}` : 'GFX';
            return (
              <div key={`gl-${t}`} className={`tl-label gfx ${hidden ? 'hidden' : ''}`} data-testid={`gfx-label-${t + 1}`}>
                <Layers size={13} /> {name}
                <span style={{ flex: 1 }} />
                {setHiddenGfx && (
                  <button className="tl-mini" aria-label={hidden ? `Show ${name} in the preview` : `Hide ${name} in the preview`} aria-pressed={hidden} data-testid={`gfx-hide-${t + 1}`}
                    title={hidden ? 'Show this graphics track in the preview' : 'Hide this graphics track in the preview (exports still draw it)'} onClick={() => toggleGfx(t)}>
                    {hidden ? <EyeOff size={11} /> : <Eye size={11} />}
                  </button>
                )}
                {canEdit && gfxCount > 1 && <button className="tl-mini" aria-label={`Remove ${name}`} title="Remove this graphics track and its graphics" data-testid={`gfx-remove-${t + 1}`}
                  onClick={() => removeGfx(t)}><Trash2 size={11} /></button>}
              </div>
            );
          })}
          {[...layers].reverse().map((l) => {
            const n = layers.indexOf(l) + 2;
            return (
              <div key={l.id} className={`tl-label layer ${l.hidden ? 'hidden' : ''} ${l.label && !/^V\d+$/.test(l.label) ? 'named' : ''}`} title={l.label} data-testid="layer-label">
                <Video size={12} /> V{n}
                {/* 1.7.2: the layer's name (e.g. Cutaways from a rough cut), cut short with an ellipsis; the full name is the title. */}
                <span style={{ flex: 1 }} />
                {l.label && !/^V\d+$/.test(l.label) && <span className="tl-label-name" data-testid="layer-name">{l.label}</span>}
                {videoTarget({ layer: l.id }, `V${n}`, `target-video-${l.id}`)}
                {trackButtons(l.id, `V${n}`)}
                <button className="tl-mini" aria-label={l.hidden ? `Show V${n}` : `Hide V${n}`} title={l.hidden ? 'Show this video track' : 'Hide this video track (it is left out of the preview and exports)'}
                  onClick={() => apply((d) => ops.updateLayer(d, l.id, { hidden: !l.hidden }))} disabled={!canEdit}>{l.hidden ? <EyeOff size={11} /> : <Eye size={11} />}</button>
                {canEdit && <button className="tl-mini" aria-label={`Remove V${n}`} title="Remove this video track and its clips"
                  onClick={() => { if (!l.clips.length || window.confirm(`Remove V${n} and its ${l.clips.length} clip${l.clips.length === 1 ? '' : 's'}?`)) apply((d) => ops.removeLayer(d, l.id)); }}><Trash2 size={11} /></button>}
              </div>
            );
          })}
          <div className="tl-label tall"><Film size={13} /> V1<span style={{ flex: 1 }} />{videoTarget('v1', 'V1', 'target-video-v1')}</div>
          <div className="tl-label"><Volume2 size={13} /> A1<span style={{ flex: 1 }} />{audioTarget('with-picture', 'A1', 'target-audio-a1')}{trackButtons('a1', 'A1')}</div>
          {audioTracks.map((t) => {
            const name = `A${t.number}`;
            const named = t.label && t.label !== name;
            return (
              <div key={t.id} className={`tl-label audio ${named ? 'named' : ''}`} data-testid={`audio-label-${t.number}`} title={named ? t.label : undefined}>
                <Music size={13} /> {name}
                {named && <span className="tl-label-name" data-testid="audio-track-name">{t.label}</span>}
                <span style={{ flex: 1 }} />
                {audioTarget({ track: t.id }, name, `target-audio-${t.id}`)}
                {trackButtons(t.id, name)}
                {canEdit && t.number > 2 && <button className="tl-mini" aria-label={`Remove ${name}`} title="Remove this audio track and its clips" data-testid={`audio-remove-${t.number}`}
                  onClick={() => removeAudioTrack(t.id, name, t.clips.length)}><Trash2 size={11} /></button>}
              </div>
            );
          })}
          <div className="tl-label"><Captions size={13} /> SUB</div>
          <div className="tl-label-spacer" />
        </div>
        <div className={`tl-lanes ${canEdit ? `tool-${trimDragMode ?? tool}` : ''}`} ref={lanes} data-tool={canEdit ? tool : undefined} onPointerMove={move} onPointerUp={end} onPointerCancel={() => end()} onScroll={onLanesScroll} onPointerDown={() => { manual.current = true; }}>
          <div style={{ width, position: 'relative' }} ref={inner}>
            <div className="tl-ruler" onPointerDown={(e) => begin(e, 'scrub', 'ruler')} role="slider" aria-label="Sequence position" aria-valuenow={Math.round(playhead / S)} tabIndex={-1}>
              {marks.map((m) => (
                <div key={m} className="tl-mark" style={{ left: m * pps }}>{!coveredMarks.has(m * pps) && <span>{tc(m * S)}</span>}</div>
              ))}
              {seqMarks}
              {(doc.markers ?? []).map((m) => {
                const ref: ItemRef = { kind: 'marker', id: m.id };
                return (
                  <div key={m.id} className={`tl-marker ${m.color ?? 'red'} ${sel('marker', m.id) ? 'selected' : ''}`} data-testid="tl-marker" data-label={m.label} data-item={itemKey(ref)}
                    style={{ left: x(m.atTicks) + moveOffset(ref, 'move-marker') }} title={`${m.label} · ${tc(m.atTicks)}${canEdit ? ' · drag to move, double-click to rename' : ''}`}
                    onPointerDown={(e) => { if (e.button === 0) setPlayhead(m.atTicks); press(e, ref, 'move-marker'); }}
                    onContextMenu={(e) => onItemMenu(e, ref)}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      if (!canEdit) return;
                      const label = window.prompt('Marker name', m.label);
                      if (label != null) apply((d2) => ops.updateMarker(d2, m.id, { label }));
                    }}>
                    <span className="tl-marker-label">{m.label}</span>
                  </div>
                );
              })}
            </div>

            {reframe && <ReframeLane doc={doc} formatId={formatView.active} selectedClipId={selectedPicture} pps={pps} playhead={playhead} setPlayhead={setPlayhead} apply={apply} seal={seal} canEdit={canEdit} />}

            {[...gfxTracks].reverse().map(graphicsLane)}

            {[...viewLayers].reverse().map((l, ri) => (
              <div key={l.id} className={`tl-lane layer ${l.hidden ? 'hidden' : ''} ${trackAudible(doc, l.id) ? '' : 'silent'}`} data-layer={l.id}
                onPointerDown={(e) => laneDown(e, { kind: 'layer', id: l.id })} onContextMenu={(e) => { if (e.target === e.currentTarget) onItemMenu(e, null); }}>
                {ri === 0 && newTrackLane('video')}
                {l.clips.map((c) => {
                  const ref: ItemRef = { kind: 'layer', id: c.id };
                  const moving = (drag?.kind === 'move-layer' && drag.id === c.id) || (inGroupMove(ref) && groupLaneDelta !== 0);
                  const lanesMoved = drag?.kind === 'move-layer' && drag.id === c.id ? laneSteps(drag.dy) : inGroupMove(ref) ? groupLaneDelta : 0;
                  const dx = moveOffset(ref, 'move-layer');
                  const di = offset('trim-layer-in', c.id);
                  const dout = offset('trim-layer-out', c.id);
                  const f = pictureFades(c, doc.frameRate);
                  const fin = liveFade('fade-in', c.id, f.inTicks);
                  const fout = liveFade('fade-out', c.id, f.outTicks);
                  return (
                    <div key={c.id} className={`tl-clip layer ${c.muted ? 'muted' : ''} ${sel('layer', c.id) ? 'selected' : ''} ${moving ? 'dragging' : ''}`} data-clip={c.id} data-item={itemKey(ref)}
                      style={{ left: x(c.recordInTicks) + dx + di, width: Math.max(6, x(clipDuration(c)) - di + dout), transform: lanesMoved ? `translateY(${lanesMoved * LANE}px)` : undefined }}
                      onPointerDown={(e) => press(e, ref, bodyKind(e, 'layer', 'move-layer'))} onContextMenu={(e) => onItemMenu(e, ref)}
                      title={`${c.label}${c.muted ? ' · muted' : ''}${c.opacity < 1 ? ` · ${Math.round(c.opacity * 100)}% opacity` : ''}${c.scale !== 1 ? ` · ${Math.round(c.scale * 100)}% size` : ''}`}>
                      <ClipWave source={sources?.get(c.versionId)} inTicks={c.sourceInTicks} outTicks={c.sourceOutTicks} width={x(clipDuration(c))} gainDb={c.muted ? -60 : c.gainDb} className="low" />
                      {fin > 0 && <span className="fade in picture" style={{ width: x(fin) }} />}
                      {fout > 0 && <span className="fade out picture" style={{ width: x(fout) }} />}
                      {canEdit && <span className={handle('left', 'trim-layer-in', c.id)} onPointerDown={(e) => pressEdge(e, ref, 'trim-layer-in')} />}
                      <span className="tl-clip-label"><LinkMark linkId={c.linkId} /><RightsMark rights={sources?.get(c.versionId)?.rights} />{c.muted && <VolumeX size={11} className="tl-muted-mark" data-testid="clip-muted" aria-label="Muted" />}{c.color && <span className="tl-cc" data-testid="cc-badge" title="Colour corrected">CC</span>}{c.label}</span>
                      {slipReadout(c.id, c.sourceInTicks)}
                      {(c.opacity < 1 || c.scale !== 1) && <span className="tl-clip-sub mono">{c.scale !== 1 ? `${Math.round(c.scale * 100)}%` : ''}{c.opacity < 1 ? ` α${Math.round(c.opacity * 100)}` : ''}</span>}
                      {fadeHandles(ref, f.inTicks, f.outTicks)}
                      {!c.muted && levelLine(c, l.id)}
                      {canEdit && <span className={handle('right', 'trim-layer-out', c.id)} onPointerDown={(e) => pressEdge(e, ref, 'trim-layer-out')} />}
                    </div>
                  );
                })}
              </div>
            ))}

            <div className="tl-lane tall v1-lane" data-testid="v1-lane" onPointerDown={(e) => laneDown(e, { kind: 'v1' })} onContextMenu={(e) => { if (e.target === e.currentTarget) onItemMenu(e, null); }}>
              {viewLayers.length === 0 && newTrackLane('video')}
              {/* 1.7.3: the gaps on V1 (black in the monitor and the export), selectable: Delete closes one. */}
              {viewGaps.map((g) => {
                const ref: ItemRef = { kind: 'gap', id: g.id };
                return (
                  <div key={`gap-${g.id}`} className={`tl-gap ${sel('gap', g.id) ? 'selected' : ''}`} data-testid={`gap-${g.id}`} data-item={itemKey(ref)}
                    style={{ left: x(g.start), width: Math.max(4, x(g.end - g.start)) }} title={`Gap · ${((g.end - g.start) / S).toFixed(2)} s${canEdit ? ' · Delete closes it' : ''}`}
                    onPointerDown={(e) => press(e, ref, 'press')} onContextMenu={(e) => onItemMenu(e, ref)} />
                );
              })}
              {viewPlaced.map((p) => {
                const c = p.clip;
                const ref: ItemRef = { kind: 'video', id: c.id };
                const dx = moveOffset(ref, 'move-video');
                const di = offset('trim-in', c.id);
                const dout = offset('trim-out', c.id);
                const f = viewFades[p.index]!;
                const fin = liveFade('fade-in', c.id, f.inTicks);
                const fout = liveFade('fade-out', c.id, f.outTicks);
                return (
                  <div key={c.id} className={`tl-clip video ${sel('video', c.id) ? 'selected' : ''} ${dx || v1Moving(ref) ? 'dragging' : ''}`} data-clip={c.id} data-item={itemKey(ref)}
                    style={{ left: x(p.recordIn) + dx + di, width: Math.max(6, x(clipDuration(c)) - di + dout), transform: v1Moving(ref) ? `translateY(${-v1Lift(vertical.v1Up)}px)` : undefined }}
                    onPointerDown={(e) => press(e, ref, bodyKind(e, 'video', 'move-video'))} onContextMenu={(e) => onItemMenu(e, ref)}>
                    {fin > 0 && <span className="fade in picture" data-testid="picture-fade-in" style={{ left: x(f.startTicks), width: x(fin) }} />}
                    {fout > 0 && <span className="fade out picture" data-testid="picture-fade-out" style={{ right: x(clipDuration(c) - f.endTicks), width: x(fout) }} />}
                    {canEdit && <span className={handle('left', 'trim-in', c.id)} data-testid="trim-handle-in" title="Drag to trim (⌘-drag ripples the later clips)" onPointerDown={(e) => pressEdge(e, ref, 'trim-in')} />}
                    <span className="tl-clip-label"><LinkMark linkId={c.linkId} /><RightsMark rights={sources?.get(c.versionId)?.rights} />{sources?.get(c.versionId)?.live && <span className="rec-badge" data-testid="clip-rec" title="From a recording still recording: it plays from the live proxy">● REC </span>}{c.color && <span className="tl-cc" data-testid="cc-badge" title="Colour corrected">CC</span>}{c.multicam ? <span className="cam-badge">{doc.multicam?.find((g) => g.id === c.multicam!.groupId) ? `Cam ${c.multicam.angle + 1}` : 'Cam'}</span> : null}{c.label}</span>
                    <span className="tl-clip-sub mono">{((clipDuration(c) - dtOf('trim-in', c.id) + dtOf('trim-out', c.id)) / S).toFixed(2)}s</span>
                    {slipReadout(c.id, c.sourceInTicks)}
                    {fadeHandles(ref, f.inTicks, f.outTicks)}
                    {canEdit && <span className={handle('right', 'trim-out', c.id)} data-testid="trim-handle-out" title="Drag to trim (⌘-drag ripples the later clips)" onPointerDown={(e) => pressEdge(e, ref, 'trim-out')} />}
                  </div>
                );
              })}
              {placed.filter((p) => p.transition).map((p) => {
                const ref: ItemRef = { kind: 'video', id: p.clip.id };
                return (
                  <div key={`t-${p.clip.id}`} className={`tl-transition ${sel('video', p.clip.id) ? 'selected' : ''}`} data-testid="timeline-transition"
                    style={{ left: x(p.recordIn) + offset('transition-in', p.clip.id), width: Math.max(8, x(p.transition!.ticks) - offset('transition-in', p.clip.id) + offset('transition-out', p.clip.id)) }}
                    title={`${TRANSITION_LABELS[p.transition!.kind]} · ${(p.transition!.ticks / S).toFixed(2)} s — drag an edge to change its length`}
                    onPointerDown={(e) => { e.stopPropagation(); if (e.button === 0) setSelection(selectOnly(doc, ref, true)); }}>
                    {canEdit && <span className="tl-handle left" data-testid="transition-edge-in" onPointerDown={(e) => pressHandle(e, ref, 'transition-in')} />}
                    {canEdit && <span className="tl-handle right" data-testid="transition-edge-out" onPointerDown={(e) => pressHandle(e, ref, 'transition-out')} />}
                    <span>{p.transition!.kind === 'dissolve' ? '╳' : p.transition!.kind === 'dip_to_black' ? '◐' : p.transition!.kind === 'wipe' ? '▶' : '⇠'}</span>
                  </div>
                );
              })}
              {/* 1.7.3: where a V1 move lands: the range it overwrites, or the line it is inserted at (⌘). */}
              {v1Drop && (v1Drop.insert
                ? <div className="tl-drop-line" data-testid="drop-insert" style={{ left: x(v1Drop.start) }} />
                : <div className="tl-drop-zone" data-testid="drop-overwrite" style={{ left: x(v1Drop.start), width: Math.max(2, x(v1Drop.end - v1Drop.start)) }} />)}
            </div>

            <div className={`tl-lane ${trackAudible(doc, 'a1') ? '' : 'silent'}`} data-testid="a1-lane" onPointerDown={(e) => laneDown(e, { kind: 'v1' })}>
              {viewPlaced.map((p) => {
                // A1 follows its storyline clip while it is moved or trimmed.
                const ref: ItemRef = { kind: 'video', id: p.clip.id };
                const di = offset('trim-in', p.clip.id);
                const dout = offset('trim-out', p.clip.id);
                // 1.7.4: a V1 clip dragged up takes its sound as many audio tracks down.
                const style = { left: x(p.recordIn) + moveOffset(ref, 'move-video') + di, width: Math.max(6, x(clipDuration(p.clip)) - di + dout), transform: v1Moving(ref) ? `translateY(${vertical.v1Up * LANE}px)` : undefined };
                // 1.7.3: a clip whose sound was unlinked shows an empty A1 (its sound is a clip on A2–A8).
                // 1.8.0: so does a clip whose source has no sound.
                if (p.clip.soundDetached || isSilent(sources, p.clip.versionId)) {
                  const silent = !p.clip.soundDetached;
                  return (
                    <div key={p.clip.id} className={`tl-clip a1 detached ${sel('video', p.clip.id) ? 'selected' : ''}`} data-clip={p.clip.id} data-testid={silent ? 'a1-silent' : 'a1-detached'} style={style}
                      title={silent ? 'The source of this clip has no sound' : 'The sound of this clip is unlinked: it is a clip on an audio track'} onPointerDown={(e) => press(e, ref, 'press')} onContextMenu={(e) => onItemMenu(e, ref)}>
                      <span className="tl-clip-label faint">{silent ? 'No sound' : 'Unlinked'}</span>
                    </div>
                  );
                }
                const fin = liveFade('fade-in', p.clip.id, p.clip.fadeInTicks);
                const fout = liveFade('fade-out', p.clip.id, p.clip.fadeOutTicks);
                return (
                  <div key={p.clip.id} className={`tl-clip a1 ${p.clip.muted ? 'muted' : ''} ${sel('video', p.clip.id) ? 'selected' : ''}`} data-clip={p.clip.id} data-item={itemKey(ref)}
                    style={style} onPointerDown={(e) => press(e, ref, 'press')} onContextMenu={(e) => onItemMenu(e, ref)}>
                    <ClipWave source={sources?.get(p.clip.versionId)} inTicks={p.clip.sourceInTicks} outTicks={p.clip.sourceOutTicks} width={x(clipDuration(p.clip))} gainDb={p.clip.gainDb} />
                    {fin > 0 && <span className="fade in" style={{ width: x(fin) }} />}
                    {fout > 0 && <span className="fade out" style={{ width: x(fout) }} />}
                    <span className="tl-clip-label">{p.clip.muted ? <VolumeX size={11} /> : `${p.clip.gainDb > 0 ? '+' : ''}${p.clip.gainDb} dB`}</span>
                    {!p.clip.muted && levelLine(p.clip, 'a1')}
                  </div>
                );
              })}
              {crossfadesOn('a1')}
            </div>

            {viewAudio.map((t, ti) => (
              <div key={t.id} className={`tl-lane audio-lane ${trackAudible(doc, t.id) ? '' : 'silent'}`} data-testid={`a${t.number}-lane`} data-track={t.id}
                onPointerDown={(e) => laneDown(e, { kind: 'audio', id: t.id })} onContextMenu={(e) => { if (e.target === e.currentTarget) onItemMenu(e, null); }}>
                {ti === viewAudio.length - 1 && newTrackLane('audio', true)}
                {crossfadesOn(t.id)}
                {t.clips.map((c) => {
                  const ref: ItemRef = { kind: 'audio', id: c.id };
                  const fin = liveFade('fade-in', c.id, c.fadeInTicks);
                  const fout = liveFade('fade-out', c.id, c.fadeOutTicks);
                  const di = offset('trim-audio-in', c.id);
                  const dout = offset('trim-audio-out', c.id);
                  // 1.7.3: dragged up or down, it is drawn on the audio track it goes to.
                  const moving = drag?.kind === 'move-audio' && drag.id === c.id && drag.moved;
                  const from = viewAudio.indexOf(t);
                  const to = moving ? Math.max(0, Math.min(Math.min(viewAudio.length, MAX_AUDIO_TRACKS), from + laneSteps(drag.dy))) : from;
                  return (
                    <div key={c.id} className={`tl-clip a2 ${t.number > 2 ? 'ax' : ''} ${c.muted ? 'muted' : ''} ${sel('audio', c.id) ? 'selected' : ''} ${moving ? 'dragging' : ''}`} data-clip={c.id} data-item={itemKey(ref)}
                      style={{ left: x(c.recordInTicks) + moveOffset(ref, 'move-audio') + di, width: Math.max(6, x(clipDuration(c)) - di + dout), transform: to !== from ? `translateY(${(to - from) * LANE}px)` : undefined }}
                      onPointerDown={(e) => press(e, ref, bodyKind(e, 'audio', 'move-audio'))} onContextMenu={(e) => onItemMenu(e, ref)}>
                      <ClipWave source={sources?.get(c.versionId)} inTicks={c.sourceInTicks} outTicks={c.sourceOutTicks} width={x(clipDuration(c))} gainDb={c.gainDb} />
                      {fin > 0 && <span className="fade in" style={{ width: x(fin) }} />}
                      {fout > 0 && <span className="fade out" style={{ width: x(fout) }} />}
                      {canEdit && <span className={handle('left', 'trim-audio-in', c.id)} data-testid="audio-trim-in" onPointerDown={(e) => pressEdge(e, ref, 'trim-audio-in')} />}
                      <span className="tl-clip-label"><LinkMark linkId={c.linkId} /><RightsMark rights={sources?.get(c.versionId)?.rights} />{c.muted && <VolumeX size={11} className="tl-muted-mark" data-testid="clip-muted" aria-label="Muted" />}{c.label}</span>
                      {slipReadout(c.id, c.sourceInTicks)}
                      {fadeHandles(ref, c.fadeInTicks, c.fadeOutTicks)}
                      {!c.muted && levelLine(c, t.id)}
                      {canEdit && <span className={handle('right', 'trim-audio-out', c.id)} data-testid="audio-trim-out" onPointerDown={(e) => pressEdge(e, ref, 'trim-audio-out')} />}
                    </div>
                  );
                })}
              </div>
            ))}

            <div className="tl-lane" data-testid="sub-lane" onPointerDown={(e) => laneDown(e)} onContextMenu={(e) => { if (e.target === e.currentTarget) onItemMenu(e, null); }}>
              {doc.subtitles?.cues.map((c) => {
                const ref: ItemRef = { kind: 'cue', id: c.id };
                // While an edge is dragged, the cue as the trim will leave it (clamped at its neighbours).
                const trimming = drag && (drag.kind === 'trim-cue-in' || drag.kind === 'trim-cue-out') && drag.id === c.id;
                const shown = trimming ? ops.trimCue(doc, c.id, drag.kind === 'trim-cue-in' ? 'in' : 'out', drag.dt, tpf).subtitles!.cues.find((k) => k.id === c.id)! : c;
                return (
                  <div key={c.id} className={`tl-clip cue ${sel('cue', c.id) ? 'selected' : ''}`} data-cue={c.id} data-item={itemKey(ref)}
                    style={{ left: x(shown.startTicks) + moveOffset(ref, 'move-cue'), width: Math.max(4, x(shown.endTicks - shown.startTicks)) }}
                    onPointerDown={(e) => press(e, ref, 'move-cue')} onContextMenu={(e) => onItemMenu(e, ref)} title={runsText(cueRuns(c.text))}>
                    {canEdit && <span className={handle('left', 'trim-cue-in', c.id)} data-testid="cue-handle-in" title="Drag to change where the cue starts"
                      onPointerDown={(e) => pressEdge(e, ref, 'trim-cue-in')} />}
                    <span className="tl-clip-label" dir="auto"><CueText cue={{ text: c.text }} /></span>
                    {canEdit && <span className={handle('right', 'trim-cue-out', c.id)} data-testid="cue-handle-out" title="Drag to change where the cue ends"
                      onPointerDown={(e) => pressEdge(e, ref, 'trim-cue-out')} />}
                  </div>
                );
              })}
            </div>

            <div className="tl-playhead" ref={playheadLine} style={{ left: x(playing ? clock.get() : playhead) }} data-testid="tl-playhead" />
            {band && (() => {
              const b = boxOf({ x: band.x0, y: band.y0 }, { x: band.x1, y: band.y1 });
              return isTinyBox(b) ? null : <div className="tl-band" data-testid="rubber-band" style={{ left: b.left, top: b.top, width: b.right - b.left, height: b.bottom - b.top }} />;
            })()}
          </div>
        </div>
      </div>
      {menu && menuEntries.length > 0 && <ContextMenu at={menu} items={menuEntries} onClose={() => setMenu(null)} label="Clip actions" />}
    </div>
  );
}
